import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Stage, Layer, Line, Circle, Rect, Text, Shape, Group } from 'react-konva';
import PlanToolbar from './elements/planToolbar.jsx';
import RoomPresetBar from './elements/roomPresetBar.jsx';
import Dar from './elements/dar.jsx';
import Panjere from './elements/panjere.jsx';
import Bazsho from './elements/bazsho.jsx';
import Otagh from './elements/otagh.jsx';
import OtaghNameBox from './elements/otaghNameBox.jsx';
import DoorFacingBox from './elements/doorFacingBox.jsx';
import PlanMagnifier from './elements/planMagnifier.jsx';
import { detectRoom, worldToScreen } from './elements/otaghUtils.jsx';
import { applyJunctionSplits } from './elements/wallSplit.jsx';
import { computeSpaces, spaceContains as spaceContainsPt } from './elements/spaces.jsx';
import SketchImport from './elements/sketchImport.jsx';
import { scaleToArea } from './elements/sketchScan.jsx';
import { insideAny } from './elements/entrance.jsx';
import {
  findSpaceAt,
  regionPolygon,
  polyToWalls,
  polyArea,
  polyCentroid,
  polyInterior,
  simplifyClosed,
  strokeToPolyline,
  zoneEdgeFlags,
  edgeNormal,
  slideEdge,
  moveCorner,
  insertVertex,
  fitAreaBySlide,
  zoneSnapTargets,
  snapZonePoint,
} from './elements/zoneSplit.jsx';
import ZoneBox from './elements/zoneBox.jsx';
import {
  PAINT_PRESETS,
  paintColor,
  buildPaintModel,
  findPaintCell,
  paintTargets,
  paintComponents,
  paintBoundaries,
  paintResult,
  PAINT_STEPS,
  cellNeighbors,
  keepLargestPieces,
  remapLabels,
  connectPath,
} from './elements/zonePaint.jsx';
import { isArc, arcFlat, arcGeom, arcPoints, expandArcs, parentWallId, maxArc } from './elements/arcWalls.jsx';
import { buildAreaModel, spaceAreas, spaceAt, solveRoomArea, makePointMap } from './elements/areaSolver.jsx';
import Create3D from './Create3D.jsx';
import './createPlan.scss';

const GRID_SIZE = 50; // هر واحد گرید = ۵۰ سانتی‌متر واقعی (۱ پیکسل = ۱ سانتی‌متر)
const WALL_THICKNESS = 8;
const DOOR_WIDTH = 80;
const WINDOW_WIDTH = 100;
const OPENING_WIDTH = 150; // عرض پیش‌فرض «بازشو» — پهن‌تر از در/پنجره، چون معمولاً برای مرز باز بین دو فضا (مثل پذیرایی و آشپزخانه) استفاده می‌شود
const WALL_CLICK_THRESHOLD = 24;
const WALL_END_ZONE_PX = 40; // شعاع ناحیه لمسی سر/ته دیوار روی صفحه (پیکسل)، مستقل از میزان زوم — هم برای گرفتن مستقیم نوک دیوار و هم برای تشخیص ناحیه هنگام لمس بدنه
const WALL_END_ZONE_MAX_RATIO = 0.4; // حداکثر سهمی از طول دیوار که به هر انتها اختصاص می‌یابد، تا در دیوارهای کوتاه هم بخشی از وسط برای جابه‌جایی کامل باز بماند
const WALL_HANDLE_VISUAL_RADIUS_PX = 7; // شعاع دایره دیداری سر/ته دیوار پس از انتخاب (پیکسل روی صفحه)
const DEFAULT_AREA_SQM = 100;
const GUIDE_MARGIN_SQM = 60;
const JOINT_SNAP_THRESHOLD = 40; // فاصله مجاز برای چسبیدن مغناطیسی به دیوار دیگر — همین محدوده «متصل» حساب می‌شود
const NEARBY_ELEMENT_HIDE_DISTANCE = 300; // بیشتر از ۳ متر: فاصله تا نزدیک‌ترین عنصر دیگر در ذره‌بین اصلاً نشان داده نشود
const TOOLBAR_HEIGHT = 52;
const ROOM_PRESET_BAR_HEIGHT = 48;
const CANVAS_TOP_OFFSET = TOOLBAR_HEIGHT + ROOM_PRESET_BAR_HEIGHT; // باید با createPlan.scss هماهنگ بماند
const MIN_FRAME_SIZE = 50; // حداقل اندازه هر ضلع کادر فضای آماده، هنگام تغییر اندازه
const ROOM_LABEL_FILL_INSET = 12; // فاصله (پدینگ) ناحیه تیره داخلی فضای آماده از خودِ دیوارها — تا دو فضای هم‌جوار در تیرگی هم ادغام نشوند
const ROOM_LABEL_MAX_FONT_PX = 15; // حداکثر اندازه فونت اسم فضا (پیکسل روی صفحه، مستقل از زوم)
const ROOM_LABEL_MIN_FONT_PX = 10; // حداقل اندازه فونت اسم، قبل از این‌که به‌جای کوچیک‌تر شدن بیشتر، با «...» کوتاه شود
const ROOM_AREA_MAX_FONT_PX = 12;
const ROOM_AREA_MIN_FONT_PX = 9;
const ROOM_LABEL_VISIBLE_MIN_PX = 26; // حداقل کوچیک‌ترین بعد چهارچوب (پیکسل روی صفحه) برای نمایش حتی فقط اسم فضا
const ROOM_AREA_VISIBLE_MIN_PX = 46; // حداقل ارتفاع چهارچوب (پیکسل روی صفحه) برای نمایش هم اسم هم مساحت با هم
const MIN_SCALE = 0.08;
const MAX_SCALE = 2.5;
const ZOOM_STEP_FACTOR = 1.25; // ضریب نسبی هر بار زوم، برای حس یکسان در همه سطوح
const ZOOM_ANIM_DURATION = 220; // میلی‌ثانیه، برای زوم نرم و تدریجی
function sqmToSidePx(areaSqm) {
  return Math.sqrt(areaSqm) * 100;
}

function metersLabel(px) {
  const meters = px / 100;
  return `${meters.toLocaleString('fa-IR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} متر`;
}

function snapToGrid(value) {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

function snapAngle(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);
  const snappedAngle = Math.round(angle / (Math.PI / 12)) * (Math.PI / 12);
  return { x: x1 + distance * Math.cos(snappedAngle), y: y1 + distance * Math.sin(snappedAngle) };
}

function pointToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  let t = lengthSq === 0 ? 0 : ((px - x1) * dx + (py - y1) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;
  const distance = Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2);
  return { distance, ratio: t };
}

// فاصله واقعی (بر حسب واحد دنیای دیوار) ناحیه لمسی سر/ته: اندازه‌ای ثابت روی صفحه (مستقل از زوم)،
// با سقفی از طول خود دیوار تا در دیوارهای خیلی کوتاه هم بخشی از وسط برای جابه‌جایی کامل باز بماند
function getWallEndZoneDistance(wall, scale) {
  const wallLength = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
  if (wallLength === 0) return 0;
  return Math.min(WALL_END_ZONE_PX / scale, wallLength * WALL_END_ZONE_MAX_RATIO);
}

// همان فاصله بالا، به‌صورت سهمی از طول دیوار (برای مقایسه با ratio خروجی pointToSegment)
function getWallEndZoneRatio(wall, scale) {
  const wallLength = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
  if (wallLength === 0) return 0;
  return getWallEndZoneDistance(wall, scale) / wallLength;
}

function findNearestWall(walls, px, py) {
  let nearest = null;
  walls.forEach((wall) => {
    const { distance, ratio } = pointToSegment(px, py, wall.x1, wall.y1, wall.x2, wall.y2);
    if (distance <= WALL_CLICK_THRESHOLD && (!nearest || distance < nearest.distance)) {
      nearest = { wallId: wall.id, ratio, wall };
    }
  });
  return nearest;
}

// تشخیصِ فضا برای ابزارِ «اتاق»: اول همان پرکردنِ گرید (detectRoom)؛ اگر فضا «بسته» نبود ولی برنامه آن را جزوِ
// خانه حساب می‌کند (درزِ باریکِ دیوارِ بیرونی — computeSpaces)، همان فضا. rowSpans با ارتفاعِ هر تکه (h)
// مدلِ مساحت فقط با دیوارها و مرزهای صاف (خط‌چینِ آزادِ «قسمت»ها شبکه را بی‌دلیل ریز و کند می‌کند)
const gridWalls = (walls) => (walls.some((w) => w.zoneOf != null) ? walls.filter((w) => w.zoneOf == null) : walls);

function detectSpace(walls, x, y) {
  const r = detectRoom(walls, x, y, WALL_THICKNESS);
  if (r.success || r.reason !== 'notEnclosed') return r;
  const sp = computeSpaces(walls, [], [], []).find((q) => spaceContainsPt(q, x, y));
  if (!sp) return r;
  const rects = sp.rects && sp.rects.length ? sp.rects : [sp];
  const big = rects.reduce((b, q) => ((q.x2 - q.x1) * (q.y2 - q.y1) > (b.x2 - b.x1) * (b.y2 - b.y1) ? q : b));
  return {
    success: true,
    rowSpans: rects.map((q) => ({ y: q.y1, x1: q.x1, x2: q.x2, h: q.y2 - q.y1 })),
    cellSize: 0,
    areaSqm: sp.area / 10000,
    centroid: { x: (big.x1 + big.x2) / 2, y: (big.y1 + big.y2) / 2 },
  };
}

// نزدیک‌ترین نقطه پایانی یک دیوار دیگر (به‌جز دیوار در حال ویرایش) برای چسبیدن مغناطیسی
function findNearbyWallEndpoint(walls, excludeWallId, point, threshold) {
  let nearest = null;
  walls.forEach((wall) => {
    if (wall.id === excludeWallId) return;
    const dStart = Math.hypot(point.x - wall.x1, point.y - wall.y1);
    if (dStart <= threshold && (!nearest || dStart < nearest.dist)) {
      nearest = { dist: dStart, x: wall.x1, y: wall.y1, farPoint: { x: wall.x2, y: wall.y2 } };
    }
    const dEnd = Math.hypot(point.x - wall.x2, point.y - wall.y2);
    if (dEnd <= threshold && (!nearest || dEnd < nearest.dist)) {
      nearest = { dist: dEnd, x: wall.x2, y: wall.y2, farPoint: { x: wall.x1, y: wall.y1 } };
    }
  });
  return nearest;
}

// نزدیک‌ترین نقطه روی بدنه یک دیوار دیگر (نه فقط سر/ته آن) — برای اتصال از نوع T به وسط یک دیوار
function findNearbyWallSegmentPoint(walls, excludeWallId, point, threshold) {
  let nearest = null;
  walls.forEach((wall) => {
    if (wall.id === excludeWallId) return;
    const { distance, ratio } = pointToSegment(point.x, point.y, wall.x1, wall.y1, wall.x2, wall.y2);
    if (distance <= threshold && (!nearest || distance < nearest.distance)) {
      const wallLength = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
      nearest = {
        distance,
        x: wall.x1 + (wall.x2 - wall.x1) * ratio,
        y: wall.y1 + (wall.y2 - wall.y1) * ratio,
        distToStart: ratio * wallLength,
        distToEnd: (1 - ratio) * wallLength,
        wall,
      };
    }
  });
  return nearest;
}

// زاویه تیز (۰ تا ۹۰ درجه) بین دو راستا — برای نمایش زاویه برخورد در اتصال از نوع T
function angleBetweenDirections(dx1, dy1, dx2, dy2) {
  const dot = dx1 * dx2 + dy1 * dy2;
  const mag1 = Math.hypot(dx1, dy1);
  const mag2 = Math.hypot(dx2, dy2);
  if (mag1 === 0 || mag2 === 0) return 0;
  const cos = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
  const angle = (Math.acos(cos) * 180) / Math.PI;
  return angle > 90 ? 180 - angle : angle;
}

// زاویه داخلی بین دو بردار که از یک نقطه مشترک (joint) شروع می‌شوند
function angleBetweenVectors(pointA, joint, pointB) {
  const v1 = { x: pointA.x - joint.x, y: pointA.y - joint.y };
  const v2 = { x: pointB.x - joint.x, y: pointB.y - joint.y };
  const dot = v1.x * v2.x + v1.y * v2.y;
  const mag1 = Math.hypot(v1.x, v1.y);
  const mag2 = Math.hypot(v2.x, v2.y);
  if (mag1 === 0 || mag2 === 0) return 0;
  const cos = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

// نقش یک دیوار در یک گروه/کادر فضای مشخص (بالا/راست/پایین/چپ)، یا null اگر جزو آن نباشد
function getFrameRoleForWallId(group, wallId) {
  if (!group) return null;
  if (wallId === group.topId) return 'top';
  if (wallId === group.rightId) return 'right';
  if (wallId === group.bottomId) return 'bottom';
  if (wallId === group.leftId) return 'left';
  return null;
}

// جست‌وجوی یک دیوار در میان همه گروه‌های فضای آماده — {group, role} یا null
function findWallGroupRole(roomGroups, wallId) {
  for (const group of roomGroups) {
    const role = getFrameRoleForWallId(group, wallId);
    if (role) return { group, role };
  }
  return null;
}

// بازسازی مختصات هر چهار دیوار یک گروه از روی محدوده (bounds) فعلی‌اش — تا مستطیل همیشه بسته و هم‌راستا بماند
function frameWallsFromBounds(group, bounds) {
  const { x1, y1, x2, y2 } = bounds;
  return {
    [group.topId]: { x1, y1, x2, y2: y1 },
    [group.rightId]: { x1: x2, y1, x2, y2 },
    [group.bottomId]: { x1: x2, y1: y2, x2: x1, y2 },
    [group.leftId]: { x1, y1: y2, x2: x1, y2: y1 },
  };
}

// محدوده فعلی یک گروه، همیشه به‌صورت زنده از روی مختصات واقعی چهار دیوار عضوش محاسبه می‌شود
// (نه یک مقدار ذخیره‌شده جدا) — اگر یکی از دیوارها دیگر وجود نداشته باشد (مثلاً حذف شده) null برمی‌گرداند
// تا بقیه اضلاع خودبه‌خود به رفتار دیوار معمولی و مستقل برگردند (self-healing)
function getGroupBounds(walls, group) {
  const top = walls.find((w) => w.id === group.topId);
  const right = walls.find((w) => w.id === group.rightId);
  const bottom = walls.find((w) => w.id === group.bottomId);
  const left = walls.find((w) => w.id === group.leftId);
  if (!top || !right || !bottom || !left) return null;
  return { x1: top.x1, y1: top.y1, x2: top.x2, y2: bottom.y1 };
}

// برای چسبیدن مغناطیسی یک لبه گروه (هنگام جابه‌جایی/تغییر اندازه) به نزدیک‌ترین دیوار دیگر هم‌راستا —
// مثلاً وقتی پذیرایی را کنار آشپزخانه می‌گذاری، دیوار مشترک دقیقاً روی هم می‌نشیند، نه چند سانت کنار هم.
// orientation: 'horizontal' برای لبه بالا/پایین (مقدار y)، 'vertical' برای لبه چپ/راست (مقدار x)
// rangeMin/rangeMax: بازه محور دیگر (برای بررسی هم‌پوشانی طول، نه فقط برخورد نوک به نوک)
function findEdgeSnapValue(walls, excludeIds, orientation, edgeValue, rangeMin, rangeMax, threshold) {
  let best = null;
  walls.forEach((wall) => {
    if (excludeIds.has(wall.id)) return;
    if (orientation === 'horizontal') {
      if (Math.abs(wall.y1 - wall.y2) > 1) return; // فقط دیوارهای افقی
      const wallXMin = Math.min(wall.x1, wall.x2);
      const wallXMax = Math.max(wall.x1, wall.x2);
      if (wallXMax < rangeMin || wallXMin > rangeMax) return;
      const d = wall.y1 - edgeValue;
      if (Math.abs(d) <= threshold && (best === null || Math.abs(d) < Math.abs(best))) best = d;
    } else {
      if (Math.abs(wall.x1 - wall.x2) > 1) return; // فقط دیوارهای عمودی
      const wallYMin = Math.min(wall.y1, wall.y2);
      const wallYMax = Math.max(wall.y1, wall.y2);
      if (wallYMax < rangeMin || wallYMin > rangeMax) return;
      const d = wall.x1 - edgeValue;
      if (Math.abs(d) <= threshold && (best === null || Math.abs(d) < Math.abs(best))) best = d;
    }
  });
  return best;
}

function getItemPlacement(wall, ratio, itemWidth) {
  const wallLength = Math.sqrt((wall.x2 - wall.x1) ** 2 + (wall.y2 - wall.y1) ** 2);
  const angleRad = Math.atan2(wall.y2 - wall.y1, wall.x2 - wall.x1);
  const angleDeg = (angleRad * 180) / Math.PI;
  const halfWidthRatio = wallLength > 0 ? itemWidth / 2 / wallLength : 0;
  const clampedRatio = Math.max(halfWidthRatio, Math.min(1 - halfWidthRatio, ratio));
  const startDist = clampedRatio * wallLength - itemWidth / 2;
  const x = wall.x1 + Math.cos(angleRad) * startDist;
  const y = wall.y1 + Math.sin(angleRad) * startDist;
  return { x, y, angleDeg, ratio: clampedRatio };
}

// رسم دستی گرید روی یک Shape واحد به‌جای صدها Line، برای عملکرد و روانی بهتر
function drawGrid(ctx) {
  const size = 2000;
  ctx.beginPath();
  ctx.strokeStyle = '#f0f0f0';
  ctx.lineWidth = 1;
  for (let i = -size; i <= size; i += GRID_SIZE) {
    ctx.moveTo(i, -size);
    ctx.lineTo(i, size);
    ctx.moveTo(-size, i);
    ctx.lineTo(size, i);
  }
  ctx.stroke();
}

const ROOM_ERROR_MESSAGES = {
  onWall: 'روی دیوار کلیک کردید، داخل فضا کلیک کنید',
  notEnclosed: 'این فضا بسته نیست — دیوارها را کامل کنید، یا با «مرز فضا» (خطِ‌چین) جدایش کنید',
  outOfBounds: 'این نقطه خارج از محدوده پلان است',
  noWalls: 'ابتدا چند دیوار رسم کنید',
  tooLarge: 'فضای انتخابی خیلی بزرگ است',
};

// شناسه‌ی بعدی بعد از بازکردنِ دوباره‌ی یک طرح (بزرگ‌ترین شناسه‌ی عددی + ۱)
function nextIdOf(list) {
  return (list || []).reduce((m, x) => (typeof x.id === 'number' && x.id >= m ? x.id + 1 : m), 1);
}

// initialPlan: وضعیتِ ذخیره‌شده‌ی همین ویرایشگر (بعد از «پایان طراحی»، با «ویرایش» دوباره باز می‌شود)
// onFinish(prepared): «پایان طراحی» در سه‌بعدی — فایل‌ها و فیلم ساخته شده‌اند؛ وضعیتِ ویرایشگر هم کنارش برمی‌گردد
// onAutosave(editorState): پلانِ نیمه‌کاره (کمی بعد از هر تغییر و موقعِ خروج) — در تبِ + می‌ماند تا بعداً ادامه داده شود
function CreatePlanEditor({ onClose, propertyArea, site, listing, initialPlan = null, onFinish = null, onAutosave = null }) {
  const init = initialPlan || {};
  const guideAreaSqm = propertyArea && propertyArea > 0 ? propertyArea : DEFAULT_AREA_SQM;
  const guideSidePx = sqmToSidePx(guideAreaSqm);

  const [activeTool, setActiveTool] = useState('wall');
  const [view3D, setView3D] = useState(false);
  const [walls, setWalls] = useState(() => init.walls || []);
  const [doors, setDoors] = useState(() => init.doors || []);
  const [windows, setWindows] = useState(() => init.windows || []);
  const [openings, setOpenings] = useState(() => init.openings || []);
  const [rooms, setRooms] = useState(() => init.rooms || []);
  const [history, setHistory] = useState(() => [
    { walls: init.walls || [], doors: init.doors || [], windows: init.windows || [], openings: init.openings || [], rooms: init.rooms || [] },
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const initialStagePos = useRef({ x: window.innerWidth / 2, y: (window.innerHeight - CANVAS_TOP_OFFSET) / 2 }).current;
  const [stagePos, setStagePos] = useState(initialStagePos);
  const stagePosRef = useRef(initialStagePos);

  const [scale, setScale] = useState(() => {
    const viewAreaSqm = guideAreaSqm + GUIDE_MARGIN_SQM;
    const viewSidePx = sqmToSidePx(viewAreaSqm);
    const availableSize = Math.min(window.innerWidth, window.innerHeight - CANVAS_TOP_OFFSET) * 0.9;
    return Math.max(MIN_SCALE, Math.min(MAX_SCALE, availableSize / viewSidePx));
  });
  const scaleAnimRef = useRef(null);

  const [drawingStart, setDrawingStart] = useState(null);
  const [previewPoint, setPreviewPoint] = useState(null);
  const [selected, setSelected] = useState(null);
  // «از روی کاغذ» (فقط دیوارها از عکس می‌آیند؛ خودِ عکس هیچ‌جا نمی‌ماند)
  const [sketchOpen, setSketchOpen] = useState(false);
  // «فضای پیشنهادی» (هایلایتِ متراژِ آگهی): پیش‌فرض مربعی وسطِ صفحه؛ بعد از پلانِ کاغذ، همان قابِ دورِ خانه
  // (هم‌مساحت) — دیوارها دقیقاً روی همین هایلایت می‌نشینند. { x, y, w, h } سانتی‌متر
  const [guideRect, setGuideRect] = useState(
    () => init.guide || { x: -guideSidePx / 2, y: -guideSidePx / 2, w: guideSidePx, h: guideSidePx }
  );
  const [confirmClear, setConfirmClear] = useState(false);
  // «ورودی»: نقطه‌ای که بازدیدِ سه‌بعدی از آن‌جا شروع می‌شود { x, y } سانتی‌متر (از کاغذ، یا ابزارِ «ورودی»)
  const [entrance, setEntrance] = useState(() => init.entrance || null);
  // انیمیشنِ «کشیده شدنِ» دیوارها بعد از پلانِ کاغذ: { t0, plan: Map(id → [شروع, پایان] ثانیه), total } | null
  const [wallAnim, setWallAnim] = useState(null);
  const [animNow, setAnimNow] = useState(0);
  useEffect(() => {
    if (!wallAnim) return undefined;
    let raf = 0;
    const tick = () => {
      const t = (performance.now() - wallAnim.t0) / 1000;
      if (t >= wallAnim.total) {
        setWallAnim(null);
        return;
      }
      setAnimNow(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [wallAnim]);
  const [dragging, setDragging] = useState(null);
  const [pendingRoom, setPendingRoom] = useState(null);
  const [roomError, setRoomError] = useState(null);
  const [magnifierPointer, setMagnifierPointer] = useState(null);
  const [magnifierAngle, setMagnifierAngle] = useState(null);
  const [magnifierDistance, setMagnifierDistance] = useState(null);
  const [magnifierConnected, setMagnifierConnected] = useState(false);
  const [magnifierJunction, setMagnifierJunction] = useState(null);
  // فهرست گروه‌های فضای آماده (اتاق‌خواب، بالکن و ...) — هر گروه فقط شناسه چهار دیوار عضو و نام را نگه می‌دارد؛
  // محدوده (bounds) هرگز اینجا ذخیره نمی‌شود و همیشه زنده از روی خودِ walls محاسبه می‌شود (getGroupBounds) تا با
  // Undo/Redo هماهنگ بماند و اگر یکی از دیوارهای عضو حذف شود، بقیه خودبه‌خود به حالت دیوار معمولی برگردند.
  // { id, label, topId, rightId, bottomId, leftId }
  const [roomGroups, setRoomGroups] = useState(() => init.roomGroups || []);
  // تنظیماتِ «فضاهای دستی» (بخش‌هایی که با کشیدنِ دیوار بسته شده‌اند، یا اتاقِ آماده‌ای که دیوارش شکست):
  // { id, seed: {x, y}, label?, floorStyle?, ceilingStyle?, coverPrompt? } — خودِ محدوده هر بار از روی دیوارها پیدا
  // می‌شود (computeSpaces)؛ seed فقط می‌گوید این تنظیمات مالِ کدام تکه است
  const [freeSpaces, setFreeSpaces] = useState(() => init.freeSpaces || []);
  const stageRef = useRef(null);
  const nextWallId = useRef(nextIdOf(init.walls));
  const nextDoorId = useRef(nextIdOf(init.doors));
  const nextWindowId = useRef(nextIdOf(init.windows));
  const nextOpeningId = useRef(nextIdOf(init.openings));
  const nextRoomId = useRef(nextIdOf(init.rooms));
  // شناسه‌ی فضاهای دستی (اتاقِ آماده‌ای که دیوارش شکست) از همین شمارنده است
  const nextRoomGroupId = useRef(Math.max(nextIdOf(init.roomGroups), nextIdOf(init.freeSpaces)));
  useEffect(() => {
    return () => {
      if (scaleAnimRef.current) cancelAnimationFrame(scaleAnimRef.current);
    };
  }, []);

  const animateScaleTo = useCallback((targetRaw) => {
    const target = Math.max(MIN_SCALE, Math.min(MAX_SCALE, targetRaw));
    if (scaleAnimRef.current) cancelAnimationFrame(scaleAnimRef.current);

    const startTime = performance.now();

    setScale((startScale) => {
      const step = (now) => {
        const elapsed = now - startTime;
        const t = Math.min(1, elapsed / ZOOM_ANIM_DURATION);
        const eased = 1 - Math.pow(1 - t, 3);
        const current = startScale + (target - startScale) * eased;
        setScale(current);
        if (t < 1) {
          scaleAnimRef.current = requestAnimationFrame(step);
        } else {
          scaleAnimRef.current = null;
        }
      };
      scaleAnimRef.current = requestAnimationFrame(step);
      return startScale;
    });
  }, []);

  const pushHistory = useCallback((snapshot) => {
    setHistory((prev) => [...prev.slice(0, historyIndex + 1), snapshot]);
    setHistoryIndex((i) => i + 1);
  }, [historyIndex]);

  // ساخت یک فضای آماده (مثل اتاق خواب، حمام و ...) از روی چیپ‌های زیر تاپ‌بار — به‌صورت یک کادر
  // چهاردیواره بدون در/پنجره، وسط ناحیه دیده‌شده، و بلافاصله در حالت انتخاب/آماده جابه‌جایی
  const handleCreateRoomPreset = (preset) => {
    const viewportCenterScreen = { x: window.innerWidth / 2, y: (window.innerHeight - CANVAS_TOP_OFFSET) / 2 };
    const centerWorld = {
      x: (viewportCenterScreen.x - stagePos.x) / scale,
      y: (viewportCenterScreen.y - stagePos.y) / scale,
    };
    const halfW = preset.width / 2;
    const halfH = preset.height / 2;
    const bounds = {
      x1: snapToGrid(centerWorld.x - halfW),
      x2: snapToGrid(centerWorld.x + halfW),
      y1: snapToGrid(centerWorld.y - halfH),
      y2: snapToGrid(centerWorld.y + halfH),
    };

    const group = {
      id: nextRoomGroupId.current++,
      label: preset.label,
      topId: nextWallId.current++,
      rightId: nextWallId.current++,
      bottomId: nextWallId.current++,
      leftId: nextWallId.current++,
    };
    const wallsByRole = frameWallsFromBounds(group, bounds);
    const newFrameWalls = [group.topId, group.rightId, group.bottomId, group.leftId].map((id) => ({
      id,
      ...wallsByRole[id],
    }));

    const newWalls = [...walls, ...newFrameWalls];
    setWalls(newWalls);
    pushHistory({ walls: newWalls, doors, windows, openings, rooms });
    setActiveTool('select');
    setSelected(null);
    // این گروه برای همیشه ماندگار است — با خروج از حالت انتخاب یا لمس جای خالی «نهایی» نمی‌شود
    setRoomGroups((prev) => [...prev, group]);
  };

  // بعد از کشیدن/جابه‌جاییِ دیوار: دیوارهای برخوردشده از محلِ برخورد می‌شکنند. اگر دیوارِ شکسته‌شده ضلعِ یک
  // «اتاقِ آماده» بود، آن اتاق دیگر کادرِ چهارضلعیِ ثابت نیست — به یک فضای دستی تبدیل می‌شود و نام و کف و سقفش را
  // نگه می‌دارد
  const splitAtJunctions = (plan, changedIds) => {
    const res = applyJunctionSplits(plan, changedIds, () => nextWallId.current++);
    if (res.splitIds.size) {
      const broken = roomGroups.filter((g) => [g.topId, g.rightId, g.bottomId, g.leftId].some((id) => res.splitIds.has(id)));
      if (broken.length) {
        const gone = new Set(broken.map((g) => g.id));
        setRoomGroups((prev) => prev.filter((g) => !gone.has(g.id)));
        setFreeSpaces((prev) => [
          ...prev,
          ...broken
            .map((g) => {
              const b = getGroupBounds(plan.walls, g);
              if (!b) return null;
              return {
                id: g.id,
                seed: { x: (b.x1 + b.x2) / 2, y: (b.y1 + b.y2) / 2 },
                label: g.label,
                floorStyle: g.floorStyle,
                ceilingStyle: g.ceilingStyle,
                coverPrompt: g.coverPrompt,
              };
            })
            .filter(Boolean),
        ]);
      }
    }
    return res;
  };

  // پلانِ کاغذ → دیوارها (جایگزینِ پلانِ فعلی) — نتیجه دقیقاً مثلِ دیوارکشیِ دستی است. مقیاس از متراژ؛ مرکز روی
  // همان «فضای پیشنهادی» (۰،۰) و هایلایت قابِ دورِ خانه می‌شود؛ دیوارها در محلِ برخورد شکسته می‌شوند؛ «ورودی» فقط
  // یک نقطه است؛ دیوارها با انیمیشن یکی‌یکی کشیده می‌شوند
  const handleSketchApply = ({ walls: pxWalls, entrance: pxEntrance, areaM2 }) => {
    const areaPx = (ws) => computeSpaces(ws.map((w, i) => ({ id: i + 1, ...w })), [], [], []).reduce((sum, sp) => sum + sp.area, 0);
    const scaled = scaleToArea(pxWalls, areaM2, areaPx, { x: 0, y: 0 });
    if (!scaled.walls.length) {
      setSketchOpen(false);
      return;
    }
    let id = 1;
    const fresh = scaled.walls.map((w) => ({ id: id++, ...w }));
    nextWallId.current = id;
    const res = applyJunctionSplits({ walls: fresh, doors: [], windows: [], openings: [] }, fresh.map((w) => w.id), () => nextWallId.current++);
    const newWalls = res.walls;
    const spaces = computeSpaces(newWalls, [], [], []);

    // «ورودی» (فقط یک نقطه — درها را کاربر خودش می‌گذارد)
    setEntrance(pxEntrance ? { x: Math.round(pxEntrance.x * scaled.scale + scaled.offset.x), y: Math.round(pxEntrance.y * scaled.scale + scaled.offset.y) } : null);

    // هایلایتِ متراژ = قابِ دورِ خانه
    const flatWalls = expandArcs(newWalls); // قوسی که بیرون زده هم داخلِ قاب باشد
    const xs = flatWalls.flatMap((w) => [w.x1, w.x2]);
    const ys = flatWalls.flatMap((w) => [w.y1, w.y2]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    setGuideRect({ x: minX, y: minY, w: maxX - minX, h: maxY - minY });

    setWalls(newWalls);
    setDoors([]);
    setWindows([]);
    setOpenings([]);
    setRooms([]);
    setRoomGroups([]);
    setFreeSpaces([]);
    setSelected(null);
    pushHistory({ walls: newWalls, doors: [], windows: [], openings: [], rooms: [] });
    setSketchOpen(false);
    setActiveTool('select');

    // ترتیبِ کشیدن: اول دورِ خانه (پشتِ سرِ هم، در جهتِ عقربه‌ها از بالا)، بعد دیوارهای داخلی
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const ang = (w) => {
      const a = Math.atan2((w.y1 + w.y2) / 2 - cy, (w.x1 + w.x2) / 2 - cx) + Math.PI / 2;
      return a < 0 ? a + Math.PI * 2 : a;
    };
    const isOuter = (w) => {
      const g = arcGeom(w);
      const mx = g.apex.x;
      const my = g.apex.y;
      const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;
      const nx = -(w.y2 - w.y1) / len;
      const ny = (w.x2 - w.x1) / len;
      return insideAny(spaces, mx + nx * 25, my + ny * 25) !== insideAny(spaces, mx - nx * 25, my - ny * 25);
    };
    const realNew = newWalls.filter((w) => !w.virtual);
    const outer = realNew.filter(isOuter).sort((a, b) => ang(a) - ang(b));
    const inner = realNew.filter((w) => !isOuter(w)).sort((a, b) => ang(a) - ang(b));
    // مرزهای پیشنهادی (از روی اسم‌ها) آخرِ کار
    const virt = newWalls.filter((w) => w.virtual);
    const lenOf = (w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
    const outerLen = outer.reduce((sum, w) => sum + lenOf(w), 0) || 1;
    const plan = new Map();
    const OUTER_T = Math.min(1.5, 0.5 + outer.length * 0.12);
    let t = 0.15;
    const angPt = (x, y) => {
      const a = Math.atan2(y - cy, x - cx) + Math.PI / 2;
      return a < 0 ? a + Math.PI * 2 : a;
    };
    outer.forEach((w) => {
      const d = (lenOf(w) / outerLen) * OUTER_T;
      // قلم در جهتِ عقربه‌ها جلو می‌رود: از سری که زاویه‌اش کمتر است
      let da = angPt(w.x2, w.y2) - angPt(w.x1, w.y1);
      if (da > Math.PI) da -= Math.PI * 2;
      if (da < -Math.PI) da += Math.PI * 2;
      plan.set(w.id, [t, t + d, da < 0]);
      t += d;
    });
    const step = inner.length ? Math.min(0.16, 0.9 / inner.length) : 0;
    inner.forEach((w, i) => {
      const s0 = t + 0.05 + i * step;
      plan.set(w.id, [s0, s0 + Math.max(0.22, Math.min(0.45, lenOf(w) / 900))]);
    });
    const vStart = Math.max(...[...plan.values()].map((v) => v[1]), 0) + 0.1;
    virt.forEach((w, i) => {
      const s0 = vStart + i * 0.15;
      plan.set(w.id, [s0, s0 + 0.4]);
    });
    const total = Math.max(...[...plan.values()].map((v) => v[1]), 0) + 0.35;
    setAnimNow(0);
    setWallAnim({ t0: performance.now(), plan, total });

    // زوم روی کلِ پلان
    const bw = Math.max(100, maxX - minX);
    const bh = Math.max(100, maxY - minY);
    const vw = window.innerWidth;
    const vh = window.innerHeight - CANVAS_TOP_OFFSET;
    const s2 = Math.max(MIN_SCALE, Math.min(MAX_SCALE, Math.min((vw * 0.86) / bw, (vh * 0.7) / (bh + 80))));
    if (scaleAnimRef.current) cancelAnimationFrame(scaleAnimRef.current);
    setScale(s2);
    const pos = { x: vw / 2 - cx * s2, y: vh / 2 - cy * s2 };
    stagePosRef.current = pos;
    setStagePos(pos);
  };

  // ---------- مساحتِ اتاق‌ها ----------
  // مدلِ مساحت (شبکه‌ی خطوطِ دیوارها) — فقط وقتی اتاقِ نام‌دار هست
  const areaModel = useMemo(() => (rooms.length ? buildAreaModel(gridWalls(walls)) : null), [walls, rooms.length]);
  const roomAreaOf = useCallback(
    (room) => {
      if (room.zone && room.zone.poly) return polyArea(room.zone.poly) / 10000;
      if (!areaModel) return room.areaSqm || 0;
      const sIdx = spaceAt(areaModel, room.centroid.x, room.centroid.y);
      if (sIdx < 0) return room.areaSqm || 0;
      return spaceAreas(areaModel)[sIdx] / 10000;
    },
    [areaModel]
  );

  // مساحتِ یک اتاق → دیوارها جابه‌جا می‌شوند. اتاق‌هایی که کاربر مساحتشان را داده ثابت می‌مانند؛ قابِ خانه (متراژِ
  // کل) هم تا جای ممکن ثابت؛ پس فضاهای «تخمینی» بزرگ/کوچک می‌شوند. nextRooms: فهرستِ اتاق‌ها با همین اتاقِ تازه
  // خروجی: true اگر انجام شد
  const applyRoomArea = (nextRooms, roomId, areaM2) => {
    const model = buildAreaModel(gridWalls(walls));
    const room = nextRooms.find((r) => r.id === roomId);
    if (!model || !room) return false;
    const target = spaceAt(model, room.centroid.x, room.centroid.y);
    if (target < 0) return false;
    const locked = nextRooms
      .filter((r) => r.id !== roomId && r.areaLocked && r.area > 0)
      .map((r) => ({ s: spaceAt(model, r.centroid.x, r.centroid.y), area: r.area * 10000 }))
      .filter((c) => c.s >= 0 && c.s !== target);
    const sol = solveRoomArea(model, target, areaM2 * 10000, locked);
    if (!sol) return false;
    const map = makePointMap(model, sol);
    const newWalls = walls.map((w) => {
      const p1 = map(w.x1, w.y1);
      const p2 = map(w.x2, w.y2);
      const moved = { ...w, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
      if (isArc(w)) {
        // خیزِ قوس به نسبتِ طولِ دیوار
        const k = Math.hypot(p2.x - p1.x, p2.y - p1.y) / (Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1);
        moved.arc = Math.round(w.arc * k);
      }
      return moved;
    });
    // اتاق‌های نام‌دار: همان نقطه در پلانِ جدید، و شکلِ تازه‌شان دوباره تشخیص داده می‌شود
    const movedRooms = nextRooms.map((r0) => {
      // خط‌چینِ «قسمت» هم با پلان کش می‌آید
      const r = r0.zone
        ? {
            ...r0,
            zone: {
              ...r0.zone,
              poly: r0.zone.poly.map(([x, y]) => {
                const m = map(x, y);
                return [m.x, m.y];
              }),
              P: r0.zone.P ? map(r0.zone.P.x, r0.zone.P.y) : r0.zone.P,
            },
          }
        : r0;
      const c = map(r.centroid.x, r.centroid.y);
      const det = detectSpace(newWalls, c.x, c.y);
      return det.success
        ? { ...r, rowSpans: det.rowSpans, cellSize: det.cellSize, centroid: det.centroid, areaSqm: det.areaSqm }
        : { ...r, centroid: c };
    });
    setWalls(newWalls);
    setRooms(movedRooms);
    setFreeSpaces((list) => list.map((f) => (f.seed ? { ...f, seed: map(f.seed.x, f.seed.y) } : f)));
    if (entrance) setEntrance(map(entrance.x, entrance.y));
    if (sol.frameChanged) {
      const a = map(guideRect.x, guideRect.y);
      const b = map(guideRect.x + guideRect.w, guideRect.y + guideRect.h);
      setGuideRect({ x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y });
    }
    pushHistory({ walls: newWalls, doors, windows, openings, rooms: movedRooms });
    return true;
  };

  const pendingAreaEstimate = useMemo(() => {
    if (!pendingRoom || pendingRoom.renamingId) return null;
    const m = buildAreaModel(gridWalls(walls));
    const sIdx = m ? spaceAt(m, pendingRoom.centroid.x, pendingRoom.centroid.y) : -1;
    return sIdx >= 0 ? spaceAreas(m)[sIdx] / 10000 : pendingRoom.areaSqm;
  }, [pendingRoom, walls]);

  // ذخیره‌ی خودکارِ نیمه‌کار
  const autosaveRef = useRef(onAutosave);
  autosaveRef.current = onAutosave;
  const saveStateRef = useRef(null);
  saveStateRef.current = { walls, doors, windows, openings, rooms, roomGroups, freeSpaces, guide: guideRect, entrance };
  const autosaveFirst = useRef(true);
  useEffect(() => {
    if (autosaveFirst.current) {
      autosaveFirst.current = false;
      return undefined;
    }
    if (!autosaveRef.current) return undefined;
    const t = setTimeout(() => autosaveRef.current && autosaveRef.current(saveStateRef.current), 800);
    return () => clearTimeout(t);
  }, [walls, doors, windows, openings, rooms, roomGroups, freeSpaces, guideRect, entrance]);
  const handleClose = () => {
    if (autosaveRef.current) autosaveRef.current(saveStateRef.current);
    onClose();
  };

  const showPlanMessage = (text) => {
    setRoomError(text);
    setTimeout(() => setRoomError(null), 3500);
  };

const handleWallDraw = (stage) => {
  const pointer = stage.getRelativePointerPosition();
  const snapped = { x: snapToGrid(pointer.x), y: snapToGrid(pointer.y) };

  if (!drawingStart) {
    setDrawingStart(snapped);
    setPreviewPoint(snapped);
    return;
  }

  const finalPoint = snapAngle(drawingStart.x, drawingStart.y, pointer.x, pointer.y);
  const snappedFinal = { x: snapToGrid(finalPoint.x), y: snapToGrid(finalPoint.y) };

  if (snappedFinal.x !== drawingStart.x || snappedFinal.y !== drawingStart.y) {
    const newWall = {
      id: nextWallId.current++,
      x1: drawingStart.x,
      y1: drawingStart.y,
      x2: snappedFinal.x,
      y2: snappedFinal.y,
      // «مرزِ فضا»: فقط فضاها را جدا می‌کند (کف/سقف/نورِ جدا)؛ در سه‌بعدی دیواری ساخته نمی‌شود و می‌شود از آن رد شد
      ...(activeTool === 'divider' ? { virtual: true } : {}),
    };
    const res = splitAtJunctions({ walls: [...walls, newWall], doors, windows, openings }, [newWall.id]);
    setWalls(res.walls);
    setDoors(res.doors);
    setWindows(res.windows);
    setOpenings(res.openings);
    pushHistory({ walls: res.walls, doors: res.doors, windows: res.windows, openings: res.openings, rooms });
    setActiveTool('select');
    setSelected({ type: 'wall', id: newWall.id });
  }
  setDrawingStart(null);
  setPreviewPoint(null);
  setMagnifierPointer(null);
  setMagnifierAngle(null);
  setMagnifierDistance(null);
  setMagnifierConnected(false);
  setMagnifierJunction(null);
};
  const handleItemPlace = (stage, itemType) => {
    const pointer = stage.getRelativePointerPosition();
    // در/پنجره/بازشو روی «مرزِ فضا» و دیوارِ منحنی گذاشته نمی‌شود
    const nearest = findNearestWall(walls.filter((w) => !w.virtual && !isArc(w)), pointer.x, pointer.y);
    if (!nearest) return;

    if (itemType === 'door') {
      const newDoor = { id: nextDoorId.current++, wallId: nearest.wallId, ratio: nearest.ratio, facing: 'out' };
      const newDoors = [...doors, newDoor];
      setDoors(newDoors);
      pushHistory({ walls, doors: newDoors, windows, openings, rooms });
      setActiveTool('select');
      setSelected({ type: 'door', id: newDoor.id });
    } else if (itemType === 'window') {
      const newWindow = { id: nextWindowId.current++, wallId: nearest.wallId, ratio: nearest.ratio };
      const newWindows = [...windows, newWindow];
      setWindows(newWindows);
      pushHistory({ walls, doors, windows: newWindows, openings, rooms });
      setActiveTool('select');
      setSelected({ type: 'window', id: newWindow.id });
    } else {
      // بازشو: تکه‌ای از دیوار که دیگر دیوار نیست، برای مرز باز بین دو فضا (مثل پذیرایی و آشپزخانه)
      const newOpening = { id: nextOpeningId.current++, wallId: nearest.wallId, ratio: nearest.ratio };
      const newOpenings = [...openings, newOpening];
      setOpenings(newOpenings);
      pushHistory({ walls, doors, windows, openings: newOpenings, rooms });
      setActiveTool('select');
      setSelected({ type: 'opening', id: newOpening.id });
    }
  };

  const handleRoomDetect = (stage) => {
    const pointer = stage.getRelativePointerPosition();
    const result = detectSpace(walls, pointer.x, pointer.y);

    if (!result.success) {
      setRoomError(ROOM_ERROR_MESSAGES[result.reason] || 'تشخیص فضا ناموفق بود');
      setTimeout(() => setRoomError(null), 2500);
      return;
    }

    const screenPos = worldToScreen(stage, result.centroid);
    setPendingRoom({ ...result, screenPos });
  };

  // «مرزبندیِ فضا»: ابزارِ «مرزِ فضا» اول اسمِ قسمت (و اختیاری مساحتش) را می‌پرسد؛ بعد کاربر مرز را با انگشت
  // می‌کشد (کج‌وکوله هم باشد صاف می‌شود و دو سرش به دیوارها می‌رسد) و یک طرف را می‌زند. هر قسمت یک چندضلعیِ بسته
  // است (room.zone.poly) با دیوارهای virtual (zoneOf = شناسه‌ی اتاق). لبه‌هایی که روی دیوارند ثابت‌اند؛ لبه‌های آزاد
  // دستگیره‌ی ↔ (جابه‌جاییِ موازی) و گوشه‌ها دستگیره‌ی ● دارند؛ «+» گوشه‌ی تازه می‌سازد.
  // zoneForm: پنجره‌ی اسم | zoneDraw: منتظرِ کشیدنِ خط { name, area } | zoneStroke: خطِ انگشت | zoneSide: منتظرِ
  // انتخابِ طرف { name, area, pl } | zoneEdit: قسمتِ در حالِ ویرایش { roomId, t0 } | zoneLive: حینِ کشیدنِ دستگیره
  const [zoneForm, setZoneForm] = useState(false);
  const [zoneDraw, setZoneDraw] = useState(null);
  const [zoneStroke, setZoneStroke] = useState(null);
  const [zoneSide, setZoneSide] = useState(null);
  const [zoneEdit, setZoneEdit] = useState(null);
  const [zoneLive, setZoneLive] = useState(null);
  const zonePolyOf = (room) => (zoneLive && zoneLive.roomId === room.id ? zoneLive.poly : room.zone.poly);
  const insideHouse = (wallList) => {
    const base = computeSpaces(wallList, [], [], []);
    return (x, y) => base.some((q) => spaceContainsPt(q, x, y, 2));
  };
  const buzz = () => {
    try {
      if (navigator.vibrate) navigator.vibrate(8);
    } catch (err) {
      /* */
    }
  };

  // «رنگ کردنِ خانه‌ها» (zonePaint.jsx): ابزارِ «مرزِ فضا» اول همین را باز می‌کند؛ کشیدنِ دستی گزینه‌ی دوم است.
  // zonePaint: { model, labels0 } | paintLabels: شناسه‌ی خانه → اسم | paintBrush: اسمِ انتخاب‌شده (null = پاک‌کن)
  const [zonePaint, setZonePaint] = useState(null);
  const [paintLabels, setPaintLabels] = useState({});
  const [paintBrush, setPaintBrush] = useState(PAINT_PRESETS[0].name);
  const [paintNames, setPaintNames] = useState(PAINT_PRESETS.map((q) => q.name));
  const [paintNameInput, setPaintNameInput] = useState(null);
  const [paintStep, setPaintStep] = useState(100);
  const paintStrokeRef = useRef(null);
  // حینِ کشیدنِ انگشت رنگ‌ها فقط در این ref عوض می‌شوند و فقط لایه‌ی رنگ دوباره کشیده می‌شود (نه کلِ ویرایشگر —
  // همان لگ). با برداشتنِ انگشت به state می‌رود (متراژِ دکمه‌ها و ...)
  const paintLabelsRef = useRef({});
  // خانه‌ی شروعِ هر قسمت (اسم → شناسه‌ی خانه): اگر قسمت دو تکه شد، تکه‌ی همین خانه می‌ماند
  const paintAnchorsRef = useRef({});
  const paintLayerRef = useRef(null);
  const startZonePaint = (step = paintStep) => {
    const base = walls.filter((w) => w.zoneOf == null);
    const model = buildPaintModel(base, rooms, step);
    if (!model.spaces.length) {
      showPlanMessage('اول دیوارهای خانه را بکشید');
      setActiveTool('select');
      return;
    }
    const used = [...new Set(Object.values(model.labels))];
    setPaintNames((prev) => [...prev, ...used.filter((n) => !prev.includes(n))]);
    setZonePaint({ model, labels0: model.labels });
    paintAnchorsRef.current = {};
    paintLabelsRef.current = { ...model.labels };
    setPaintLabels(paintLabelsRef.current);
  };
  // اندازه‌ی خانه‌ها: خانه‌بندی از نو، رنگ‌های فعلی روی خانه‌های تازه می‌مانند
  const changePaintStep = (dir) => {
    const k = PAINT_STEPS.indexOf(paintStep) + dir;
    if (k < 0 || k >= PAINT_STEPS.length || !zonePaint) return;
    const step = PAINT_STEPS[k];
    const base = walls.filter((w) => w.zoneOf == null);
    const model = buildPaintModel(base, rooms, step);
    const labels = remapLabels(zonePaint.model, paintLabelsRef.current, model);
    setPaintStep(step);
    setZonePaint({ model, labels0: model.labels });
    paintLabelsRef.current = labels;
    paintAnchorsRef.current = {};
    setPaintLabels(labels);
  };
  const redrawPaint = () => {
    const layer = paintLayerRef.current;
    if (layer) layer.batchDraw();
  };
  // یک خانه (و تکه‌های باریکِ کنارش) را رنگ/پاک می‌کند. قسمت همیشه یک‌تکه است:
  // - خانه‌ای دور از قسمت زده شد → خانه‌های خالیِ بینِ راه خودکار رنگ می‌شوند (connectPath)
  // - خانه‌ای از وسطِ قسمت پاک شد (یا رنگِ دیگری رویش آمد) → تکه‌ای که از خانه‌ی شروعِ قسمت جدا افتاد خودکار پاک می‌شود
  const paintAt = (p, st) => {
    const cell = findPaintCell(zonePaint.model, p.x, p.y);
    if (!cell) return null;
    const L = paintLabelsRef.current;
    const name = st.mode === 'erase' ? null : paintBrush;
    if ((L[cell.id] || null) === name) return cell;
    let cells = [cell];
    if (name) {
      const exists = Object.values(L).includes(name);
      if (!exists) paintAnchorsRef.current[name] = cell.id;
      else if (!cellNeighbors(zonePaint.model, cell).some((q) => L[q.id] === name)) {
        const path = connectPath(zonePaint.model, L, cell, name);
        if (!path) {
          st.blocked = true;
          return cell;
        }
        cells = [cell, ...path];
      }
    }
    const spaces = new Set();
    cells.forEach((c0) =>
      paintTargets(zonePaint.model, c0).forEach((c) => {
        if (L[c.id]) st.touched.add(L[c.id]);
        if (name) L[c.id] = name;
        else delete L[c.id];
        spaces.add(c.s);
        st.spaces.add(c.s);
      })
    );
    const dropped = keepLargestPieces(zonePaint.model, L, spaces, paintAnchorsRef.current);
    dropped.forEach((n) => st.dropped.add(n));
    st.changed = true;
    redrawPaint();
    return cell;
  };
  const finishPaintStroke = () => {
    const st = paintStrokeRef.current;
    paintStrokeRef.current = null;
    if (!st || !zonePaint) return;
    const L = paintLabelsRef.current;
    if (st.tapErase && !st.changed && Math.hypot(st.last.x - st.start.x, st.last.y - st.start.y) * scale < 12) {
      paintTargets(zonePaint.model, st.tapErase).forEach((c) => {
        delete L[c.id];
        st.spaces.add(c.s);
      });
      st.changed = true;
      st.blocked = false;
      keepLargestPieces(zonePaint.model, L, st.spaces, paintAnchorsRef.current).forEach((n) => st.dropped.add(n));
    }
    const dropped = [...st.dropped];
    paintLabelsRef.current = { ...L };
    setPaintLabels(paintLabelsRef.current);
    redrawPaint();
    if (dropped.length) showPlanMessage(`«${dropped.join('، ')}» یک‌تکه ماند — بخشی که جدا افتاده بود خودکار برداشته شد`);
    else if (st.blocked && !st.changed)
      showPlanMessage(`«${paintBrush}» فقط می‌تواند یک‌تکه باشد و این‌جا راهی به آن نیست (پشتِ دیوار یا قسمتِ دیگر)`);
  };
  const paintComps = useMemo(() => (zonePaint ? paintComponents(zonePaint.model, paintLabels) : []), [zonePaint, paintLabels]);
  const paintAreas = useMemo(() => {
    const m = {};
    paintComps.forEach((c) => {
      m[c.name] = (m[c.name] || 0) + c.area;
    });
    return m;
  }, [paintComps]);
  const closeZonePaint = () => {
    setZonePaint(null);
    setPaintLabels({});
    paintLabelsRef.current = {};
    setPaintNameInput(null);
    paintStrokeRef.current = null;
  };
  const applyZonePaint = () => {
    const base = walls.filter((w) => w.zoneOf == null);
    const res = paintResult(zonePaint.model, zonePaint.labels0, paintLabelsRef.current, base);
    const removed = new Set(res.removeRoomIds);
    let newWalls = walls.filter((w) => w.zoneOf == null || !removed.has(w.zoneOf));
    let newRooms = rooms.filter((r) => !removed.has(r.id)).map((r) => {
      const rn = res.renames.find((q) => q.id === r.id);
      return rn ? { ...r, name: rn.name } : r;
    });
    const added = [];
    res.zones.forEach((z) => {
      const roomId = nextRoomId.current++;
      newWalls = [...newWalls, ...polyToWalls(z.poly, base, roomId, () => nextWallId.current++)];
      added.push({ id: roomId, name: z.name, zone: { poly: z.poly, P: z.P } });
    });
    res.plains.forEach((q) => added.push({ id: nextRoomId.current++, name: q.name, P: q.P }));
    added.forEach((a) => {
      const P = a.zone ? a.zone.P : a.P;
      const det = detectSpace(newWalls, P.x, P.y);
      newRooms.push({
        id: a.id,
        rowSpans: det.success ? det.rowSpans : [],
        cellSize: det.success ? det.cellSize : 0,
        centroid: det.success ? det.centroid : P,
        areaSqm: det.success ? det.areaSqm : a.zone ? polyArea(a.zone.poly) / 10000 : 0,
        name: a.name,
        areaLocked: false,
        area: null,
        ...(a.zone ? { zone: a.zone } : {}),
      });
    });
    // اتاق‌هایی که فقط اسمشان عوض شد، فضایشان را دوباره بسنجند (مرزهای کنارشان شاید عوض شده)
    newRooms = newRooms.map((r) => {
      if (r.zone || !res.renames.some((q) => q.id === r.id)) return r;
      const det = detectSpace(newWalls, r.centroid.x, r.centroid.y);
      return det.success ? { ...r, rowSpans: det.rowSpans, cellSize: det.cellSize, centroid: det.centroid, areaSqm: det.areaSqm } : r;
    });
    closeZonePaint();
    setActiveTool('select');
    if (!res.removeRoomIds.length && !res.renames.length && !added.length) return;
    setWalls(newWalls);
    setRooms(newRooms);
    pushHistory({ walls: newWalls, doors, windows, openings, rooms: newRooms });
    showPlanMessage(`${(added.length + res.renames.length).toLocaleString('fa-IR')} قسمت ساخته شد — برای جابه‌جاییِ ریزِ مرز، قسمت را لمس کنید`);
  };

  const finishZoneStroke = () => {
    const pl = strokeToPolyline(zoneStroke, walls, Math.max(18, 16 / scale));
    setZoneStroke(null);
    if (!pl) {
      showPlanMessage('مرز را از یک دیوار تا دیوارِ دیگر بکشید');
      return;
    }
    setZoneSide({ ...zoneDraw, pl });
    setZoneDraw(null);
  };

  const pickZoneSide = (P) => {
    const { name, area, pl } = zoneSide;
    const tmp = [...walls, ...pl.slice(0, -1).map((p, k) => ({ id: `zs${k}`, x1: p[0], y1: p[1], x2: pl[k + 1][0], y2: pl[k + 1][1], virtual: true }))];
    const { space: before } = findSpaceAt(walls, P.x, P.y);
    const { space: after } = findSpaceAt(tmp, P.x, P.y);
    if (!before || !after) {
      showPlanMessage('داخلِ همان فضایی که مرز کشیدید بزنید');
      return;
    }
    if (after.area >= before.area * 0.97) {
      showPlanMessage('این خط فضا را دو تکه نکرد — دوباره از دیوار تا دیوار بکشید');
      setZoneSide(null);
      setZoneDraw({ name, area });
      return;
    }
    const outline = regionPolygon(tmp, P);
    if (!outline) return;
    let poly = simplifyClosed(outline, 2);
    // مساحتی که کاربر داد: اگر فقط یک لبه‌ی آزاد هست، همان لبه جابه‌جا می‌شود
    if (area) {
      const free = zoneEdgeFlags(poly, walls, -1)
        .map((f, k) => (f ? -1 : k))
        .filter((k) => k >= 0);
      if (free.length === 1) poly = fitAreaBySlide(poly, free[0], area * 10000, insideHouse(walls));
    }
    poly = poly.map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
    const roomId = nextRoomId.current++;
    const newWalls = [...walls, ...polyToWalls(poly, walls, roomId, () => nextWallId.current++)];
    const ip = polyInterior(poly, P);
    const det = detectSpace(newWalls, ip.x, ip.y);
    const newRooms = [
      ...rooms,
      {
        id: roomId,
        rowSpans: det.success ? det.rowSpans : [],
        cellSize: det.success ? det.cellSize : 0,
        centroid: det.success ? det.centroid : ip,
        areaSqm: det.success ? det.areaSqm : polyArea(poly) / 10000,
        name,
        areaLocked: false,
        area: null,
        zone: { poly, P: ip },
      },
    ];
    setWalls(newWalls);
    setRooms(newRooms);
    pushHistory({ walls: newWalls, doors, windows, openings, rooms: newRooms });
    setZoneSide(null);
    setZoneEdit({ roomId, t0: performance.now() });
    setActiveTool('select');
    setSelected({ type: 'room', id: roomId });
  };

  const zoneEditRoom = zoneEdit ? rooms.find((r) => r.id === zoneEdit.roomId && r.zone) : null;
  // لبه‌های ثابت (روی دیوار) هر قسمت — حینِ کشیدن همان قبلی
  const zoneFlags = useMemo(() => {
    const m = new Map();
    rooms.forEach((r) => {
      if (r.zone && r.zone.poly && r.zone.poly.length >= 3) m.set(r.id, zoneEdgeFlags(r.zone.poly, walls, r.id));
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, dragging ? null : walls]);
  // مساحتِ «بقیه‌ی فضا» کنارِ قسمتِ در حالِ ویرایش
  const zoneBaseArea = useMemo(() => {
    if (!zoneEditRoom) return 0;
    const ip = polyInterior(zoneEditRoom.zone.poly, zoneEditRoom.zone.P);
    const { space } = findSpaceAt(
      walls.filter((w) => w.zoneOf !== zoneEditRoom.id),
      ip.x,
      ip.y
    );
    return space ? space.area : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneEditRoom && zoneEditRoom.id, dragging ? null : walls]);

  // اتاقِ قسمت‌دار انتخاب شد → ویرایشِ قسمت
  useEffect(() => {
    if (selected?.type !== 'room') return;
    const r = rooms.find((q) => q.id === selected.id);
    if (r && r.zone && (!zoneEdit || zoneEdit.roomId !== r.id)) {
      setZoneEdit({ roomId: r.id, t0: performance.now() });
      // قسمت‌های نسخه‌ی قبل (خط‌چینِ کشسان با رأس‌های زیاد) → فقط گوشه‌های واقعی
      if (r.zone.poly && r.zone.poly.length > 16) {
        const poly = simplifyClosed(r.zone.poly, 3);
        setRooms((rs) => rs.map((q) => (q.id === r.id ? { ...q, zone: { ...q.zone, poly } } : q)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const startZoneHandle = (e, room, kind, i) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    const pointer = stageRef.current.getRelativePointerPosition();
    setSelected({ type: 'room', id: room.id });
    setZoneEdit((z) => (z && z.roomId === room.id ? z : { roomId: room.id, t0: performance.now() }));
    setDragging({
      type: kind,
      roomId: room.id,
      i,
      poly0: room.zone.poly,
      flags0: zoneFlags.get(room.id) || room.zone.poly.map(() => false),
      startPointer: pointer,
      T: zoneSnapTargets(walls, rooms, room.id),
      tol: Math.min(30, Math.max(8, 14 / scale)),
      inside: insideHouse(walls.filter((w) => w.zoneOf !== room.id)),
      moved: false,
      snapped: false,
    });
  };
  const moveZoneHandle = (p) => {
    const d = dragging;
    const dx = p.x - d.startPointer.x;
    const dy = p.y - d.startPointer.y;
    if (!d.moved && Math.hypot(dx, dy) * scale < 3) return;
    let poly;
    let guides = [];
    let hot = null;
    if (d.type === 'zoneEdge') {
      const { n } = edgeNormal(d.poly0, d.i);
      let s = dx * n[0] + dy * n[1];
      // لبه‌ی افقی/عمودی: هم‌راستا با امتدادِ دیوارها
      const a = d.poly0[d.i];
      if (Math.abs(n[0]) > 0.99 || Math.abs(n[1]) > 0.99) {
        const vertical = Math.abs(n[0]) > 0.99;
        const cur = vertical ? a[0] + n[0] * s : a[1] + n[1] * s;
        let best = null;
        (vertical ? d.T.xs : d.T.ys).forEach((v) => {
          if (Math.abs(v - cur) <= d.tol && (best == null || Math.abs(v - cur) < Math.abs(best - cur))) best = v;
        });
        if (best != null) {
          s = vertical ? (best - a[0]) / n[0] : (best - a[1]) / n[1];
          guides = [vertical ? { x: best } : { y: best }];
        }
      }
      poly = slideEdge(d.poly0, d.i, s);
    } else {
      const v = d.poly0[d.i];
      const tgt = snapZonePoint(v[0] + dx, v[1] + dy, d.T, d.tol);
      guides = tgt.guides;
      poly = moveCorner(d.poly0, d.i, [tgt.x, tgt.y], d.flags0);
      if (tgt.stuck || guides.length) hot = poly[d.i];
    }
    // داخلِ خانه و بزرگ‌تر از نیم متر مربع بماند — وگرنه همان شکلِ قبلی
    if (polyArea(poly) < 5000 || !poly.every(([x, y]) => d.inside(x, y))) return;
    const snappedNow = guides.length > 0 || Boolean(hot);
    if (snappedNow && !d.snapped) buzz();
    if (!d.moved || snappedNow !== d.snapped) setDragging((q) => (q ? { ...q, moved: true, snapped: snappedNow } : q));
    setZoneLive({ roomId: d.roomId, poly, guides, hot });
  };
  const finishZoneDrag = () => {
    const room = rooms.find((r) => r.id === dragging.roomId);
    if (!room || !zoneLive || !dragging.moved) {
      setZoneLive(null);
      return;
    }
    const poly = zoneLive.poly.map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10]);
    const rest = walls.filter((w) => w.zoneOf !== room.id);
    const newWalls = [...rest, ...polyToWalls(poly, rest, room.id, () => nextWallId.current++)];
    const ip = polyInterior(poly, room.zone.P);
    const det = detectSpace(newWalls, ip.x, ip.y);
    const newRooms = rooms.map((r) =>
      r.id === room.id
        ? {
            ...r,
            zone: { ...r.zone, poly, P: ip },
            ...(det.success ? { rowSpans: det.rowSpans, cellSize: det.cellSize, centroid: det.centroid, areaSqm: det.areaSqm } : {}),
          }
        : r
    );
    setWalls(newWalls);
    setRooms(newRooms);
    setZoneLive(null);
    pushHistory({ walls: newWalls, doors, windows, openings, rooms: newRooms });
  };
  // «+»: گوشه‌ی تازه روی یک لبه‌ی آزاد
  const addZoneCorner = (e, room, i) => {
    e.cancelBubble = true;
    const poly = insertVertex(room.zone.poly, i, 0.5);
    const newRooms = rooms.map((r) => (r.id === room.id ? { ...r, zone: { ...r.zone, poly } } : r));
    setRooms(newRooms);
    pushHistory({ walls, doors, windows, openings, rooms: newRooms });
  };
  // موجِ محو — فقط همین لایه هر فریم دوباره کشیده می‌شود
  const zoneWaveRef = useRef(null);
  useEffect(() => {
    if (!zoneEdit) return undefined;
    let raf = 0;
    const tick = () => {
      const node = zoneWaveRef.current;
      if (node && node.getLayer()) node.getLayer().batchDraw();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [zoneEdit]);

  const handleStagePointerDown = (e) => {
    const stage = stageRef.current;
    if (activeTool === 'divider' && zonePaint) {
      const p = stage.getRelativePointerPosition();
      const cell = findPaintCell(zonePaint.model, p.x, p.y);
      if (cell) {
        // لمسِ خانه‌ای که همین رنگ را دارد = پاک کردن (تا آخرِ همین کشیدن)
        // پاک‌کن یا «یک ضربه» روی خانه‌ای با همین رنگ = پاک کردن؛ کشیدن از روی همین رنگ = ادامه دادنِ همان قسمت
        const mode = !paintBrush ? 'erase' : 'paint';
        const tapErase = paintBrush && paintLabelsRef.current[cell.id] === paintBrush ? cell : null;
        paintStrokeRef.current = { mode, last: p, start: p, tapErase, touched: new Set(), spaces: new Set(), dropped: new Set(), changed: false, blocked: false };
        paintAt(p, paintStrokeRef.current);
      } else {
        // بیرونِ خانه: جابه‌جا کردنِ صفحه
        stagePosRef.current = { ...stagePos };
        setDragging({ type: 'pan', startPointer: stage.getPointerPosition(), startStagePos: { ...stagePosRef.current } });
      }
      return;
    }
    if (activeTool === 'divider') {
      const p = stage.getRelativePointerPosition();
      if (zoneSide) pickZoneSide(p);
      else if (zoneDraw) setZoneStroke([[p.x, p.y]]);
      return;
    }
if (activeTool === 'wall' || activeTool === 'divider') {
  const wasStarting = !drawingStart;
  handleWallDraw(stage);
  if (wasStarting) {
    const pointer = stage.getRelativePointerPosition();
    setMagnifierPointer(pointer);
  }
} else if (activeTool === 'door' || activeTool === 'window' || activeTool === 'opening') {
      handleItemPlace(stage, activeTool);
    } else if (activeTool === 'room') {
      handleRoomDetect(stage);
    } else if (activeTool === 'entrance') {
      const p = stage.getRelativePointerPosition();
      setEntrance({ x: Math.round(p.x), y: Math.round(p.y) });
      setActiveTool('select');
      setSelected({ type: 'entrance', id: 0 });
    } else if (activeTool === 'select') {
      if (e.target === stage) {
        setSelected(null);
        // لمس جای خالی یعنی دیگر روی هیچ عنصری کار نمی‌شود؛ ذره‌بین باید ناپدید شود
        setMagnifierPointer(null);
        setMagnifierAngle(null);
        setMagnifierDistance(null);
        setMagnifierConnected(false);
        setMagnifierJunction(null);
        const absPointer = stage.getPointerPosition();
        stagePosRef.current = { ...stagePos };
        setDragging({ type: 'pan', startPointer: absPointer, startStagePos: { ...stagePosRef.current } });
      }
    }
  };

  const handleStagePointerMove = () => {
    const stage = stageRef.current;
    if (paintStrokeRef.current && zonePaint) {
      const p = stage.getRelativePointerPosition();
      const st = paintStrokeRef.current;
      const d = Math.hypot(p.x - st.last.x, p.y - st.last.y);
      const n = Math.max(1, Math.ceil(d / 20));
      for (let k = 1; k <= n; k += 1) {
        paintAt({ x: st.last.x + ((p.x - st.last.x) * k) / n, y: st.last.y + ((p.y - st.last.y) * k) / n }, st);
      }
      st.last = p;
      return;
    }
    if (zoneStroke) {
      const p = stage.getRelativePointerPosition();
      const last = zoneStroke[zoneStroke.length - 1];
      if (Math.hypot(p.x - last[0], p.y - last[1]) * scale > 3) setZoneStroke((q) => (q ? [...q, [p.x, p.y]] : q));
      return;
    }

    if (dragging) {
      if (dragging.type === 'zoneEdge' || dragging.type === 'zoneCorner') {
        moveZoneHandle(stage.getRelativePointerPosition());
        return;
      }
      if (dragging.type === 'pan') {
        // در حین Pan مستقیماً روی Konva اعمال می‌شود (بدون setState) تا حرکت کاملاً روان بماند
        const absPointer = stage.getPointerPosition();
        const dx = absPointer.x - dragging.startPointer.x;
        const dy = absPointer.y - dragging.startPointer.y;
        const newPos = { x: dragging.startStagePos.x + dx, y: dragging.startStagePos.y + dy };
        stagePosRef.current = newPos;
        stage.position(newPos);
        stage.batchDraw();
        return;
      }

      const pointer = stage.getRelativePointerPosition();

      if (dragging.type === 'entrance') {
        setEntrance({ x: Math.round(pointer.x), y: Math.round(pointer.y) });
        return;
      }

      if (dragging.type === 'bend') {
        setWalls((prev) =>
          prev.map((w) => {
            if (w.id !== dragging.id) return w;
            const g = arcGeom({ ...w, arc: 0 });
            let h = (pointer.x - g.mid.x) * g.nx + (pointer.y - g.mid.y) * g.ny;
            if (Math.abs(h) < 12) h = 0; // نزدیکِ خطِ راست = صاف
            h = Math.max(-maxArc(w), Math.min(maxArc(w), Math.round(h)));
            return { ...w, arc: h };
          })
        );
        return;
      }

      if (dragging.type === 'wall') {
        if (dragging.mode === 'start' || dragging.mode === 'end') {
          const wall = walls.find((w) => w.id === dragging.id);
          const farPoint = dragging.mode === 'start' ? { x: wall.x2, y: wall.y2 } : { x: wall.x1, y: wall.y1 };
          // شعاع چسبیدن مغناطیسی واقعی (تنگ) — فقط در همین محدوده نقطه واقعاً به سر/ته دیوار دیگر می‌چسبد (اتصال گوشه‌ای)
          const nearbyEndpoint = findNearbyWallEndpoint(walls, dragging.id, pointer, JOINT_SNAP_THRESHOLD);
          // اگر به هیچ سر/ته‌ای نچسبید، شاید دارد به وسط بدنه یک دیوار دیگر می‌چسبد (اتصال از نوع T)
          const nearbySegment = !nearbyEndpoint ? findNearbyWallSegmentPoint(walls, dragging.id, pointer, JOINT_SNAP_THRESHOLD) : null;
          // شعاع جست‌وجوی گسترده‌تر — فقط برای نمایش اطلاعاتی «چقدر با نزدیک‌ترین دیوار دیگر فاصله داری»
          const nearbyForDisplay = nearbyEndpoint || findNearbyWallEndpoint(walls, dragging.id, pointer, NEARBY_ELEMENT_HIDE_DISTANCE);

          let finalPoint = pointer;
          let angleDeg = null;
          let isConnected = false;
          let junction = null;

          if (nearbyEndpoint) {
            // اتصال گوشه‌ای: سر/ته این دیوار دقیقاً روی سر/ته دیوار دیگر می‌نشیند
            finalPoint = { x: nearbyEndpoint.x, y: nearbyEndpoint.y };
            angleDeg = angleBetweenVectors(farPoint, finalPoint, nearbyEndpoint.farPoint);
            isConnected = true;
          } else if (nearbySegment) {
            // اتصال از نوع T: این دیوار به وسط بدنه دیوار دیگر می‌چسبد
            finalPoint = { x: nearbySegment.x, y: nearbySegment.y };
            angleDeg = angleBetweenDirections(
              finalPoint.x - farPoint.x,
              finalPoint.y - farPoint.y,
              nearbySegment.wall.x2 - nearbySegment.wall.x1,
              nearbySegment.wall.y2 - nearbySegment.wall.y1
            );
            isConnected = true;
            junction = { toStart: metersLabel(nearbySegment.distToStart), toEnd: metersLabel(nearbySegment.distToEnd) };
          }

          let baseDistance = null;
          if (!isConnected) {
            baseDistance = nearbyForDisplay
              ? Math.hypot(pointer.x - nearbyForDisplay.x, pointer.y - nearbyForDisplay.y) // فاصله تا همان دیوار ثابت دیگر
              : Math.hypot(finalPoint.x - farPoint.x, finalPoint.y - farPoint.y); // هیچ دیواری نزدیک نیست؛ طول فعلی خود دیوار
          }

          // فاصله را فقط وقتی نشان بده که نه متصل شده، نه بیش از حد دور/بزرگ است
          const distanceLabel = !isConnected && baseDistance !== null && baseDistance <= NEARBY_ELEMENT_HIDE_DISTANCE
            ? metersLabel(baseDistance)
            : null;

          setMagnifierAngle(angleDeg);
          setMagnifierDistance(distanceLabel);
          // «متصل» ساده فقط برای اتصال گوشه‌ای معنا دارد؛ برای اتصال T به‌جایش دو فاصله سر/ته نشان داده می‌شود
          setMagnifierConnected(isConnected && !junction);
          setMagnifierJunction(junction);
          setMagnifierPointer(finalPoint);

          const key = dragging.mode === 'start' ? { x1: finalPoint.x, y1: finalPoint.y } : { x2: finalPoint.x, y2: finalPoint.y };
          setWalls((prev) => prev.map((w) => (w.id === dragging.id ? { ...w, ...key } : w)));
        } else if (dragging.mode === 'move') {
          setMagnifierPointer(pointer);
          setMagnifierAngle(null);
          setMagnifierDistance(null);
          setMagnifierConnected(false);
          setMagnifierJunction(null);
          const dx = pointer.x - dragging.startPointer.x;
          const dy = pointer.y - dragging.startPointer.y;
          setWalls((prev) =>
            prev.map((w) =>
              w.id === dragging.id
                ? {
                    ...w,
                    x1: dragging.original.x1 + dx,
                    y1: dragging.original.y1 + dy,
                    x2: dragging.original.x2 + dx,
                    y2: dragging.original.y2 + dy,
                  }
                : w
            )
          );
        }
        return;
      }

      if (dragging.type === 'door' || dragging.type === 'window' || dragging.type === 'opening') {
        setMagnifierPointer(pointer);
        setMagnifierAngle(null);
        setMagnifierDistance(null);
        setMagnifierConnected(false);
        setMagnifierJunction(null);
        const wall = walls.find((w) => w.id === dragging.wallId);
        if (!wall) return;
        const { ratio } = pointToSegment(pointer.x, pointer.y, wall.x1, wall.y1, wall.x2, wall.y2);
        if (dragging.type === 'door') {
          setDoors((prev) => prev.map((d) => (d.id === dragging.id ? { ...d, ratio } : d)));
        } else if (dragging.type === 'window') {
          setWindows((prev) => prev.map((wn) => (wn.id === dragging.id ? { ...wn, ratio } : wn)));
        } else {
          setOpenings((prev) => prev.map((o) => (o.id === dragging.id ? { ...o, ratio } : o)));
        }
        return;
      }

      if (dragging.type === 'group-move') {
        // جابه‌جایی کل گروه (لمس وسط/داخل فضا) — فقط انتقال، بدون تغییر اندازه؛ این رفتار همیشگی و دائمی است
        const group = roomGroups.find((g) => g.id === dragging.groupId);
        if (group) {
          const dx = pointer.x - dragging.startPointer.x;
          const dy = pointer.y - dragging.startPointer.y;
          const newBounds = {
            x1: dragging.originalBounds.x1 + dx,
            y1: dragging.originalBounds.y1 + dy,
            x2: dragging.originalBounds.x2 + dx,
            y2: dragging.originalBounds.y2 + dy,
          };

          // چسبیدن مغناطیسی کل گروه به نزدیک‌ترین دیوار دیگر هم‌راستا (مثلاً وقتی پذیرایی را کنار آشپزخانه
          // می‌گذاری) — تا دیوار مشترک دقیقاً روی هم بنشیند، نه چند سانت کنار هم با یک شکاف ناخواسته
          const ownIds = new Set([group.topId, group.rightId, group.bottomId, group.leftId]);
          const snapTop = findEdgeSnapValue(walls, ownIds, 'horizontal', newBounds.y1, newBounds.x1, newBounds.x2, JOINT_SNAP_THRESHOLD);
          const snapBottom = findEdgeSnapValue(walls, ownIds, 'horizontal', newBounds.y2, newBounds.x1, newBounds.x2, JOINT_SNAP_THRESHOLD);
          const snapDy = snapTop !== null && (snapBottom === null || Math.abs(snapTop) <= Math.abs(snapBottom)) ? snapTop : snapBottom;
          const snapLeft = findEdgeSnapValue(walls, ownIds, 'vertical', newBounds.x1, newBounds.y1, newBounds.y2, JOINT_SNAP_THRESHOLD);
          const snapRight = findEdgeSnapValue(walls, ownIds, 'vertical', newBounds.x2, newBounds.y1, newBounds.y2, JOINT_SNAP_THRESHOLD);
          const snapDx = snapLeft !== null && (snapRight === null || Math.abs(snapLeft) <= Math.abs(snapRight)) ? snapLeft : snapRight;

          if (snapDy) {
            newBounds.y1 += snapDy;
            newBounds.y2 += snapDy;
          }
          if (snapDx) {
            newBounds.x1 += snapDx;
            newBounds.x2 += snapDx;
          }

          const wallsByRole = frameWallsFromBounds(group, newBounds);
          setWalls((prev) => prev.map((w) => (wallsByRole[w.id] ? { ...w, ...wallsByRole[w.id] } : w)));
        }
        return;
      }

      if (dragging.type === 'group-resize') {
        // کشیدن یکی از چهار ضلع گروه — فقط همان ضلع در راستای عمود بر خودش جابه‌جا می‌شود و مساحت داخل
        // فضا همزمان تغییر می‌کند؛ دو ضلع مجاور به همراهش کش می‌آیند. این ضلع هرگز اجازه جدا شدن (کشیدن آزاد نوک) ندارد
        const group = roomGroups.find((g) => g.id === dragging.groupId);
        const bounds = group ? getGroupBounds(walls, group) : null;
        if (group && bounds) {
          const ownIds = new Set([group.topId, group.rightId, group.bottomId, group.leftId]);
          if (dragging.role === 'top') {
            let y1 = Math.min(snapToGrid(pointer.y), bounds.y2 - MIN_FRAME_SIZE);
            const snap = findEdgeSnapValue(walls, ownIds, 'horizontal', y1, bounds.x1, bounds.x2, JOINT_SNAP_THRESHOLD);
            if (snap !== null) y1 += snap;
            bounds.y1 = Math.min(y1, bounds.y2 - MIN_FRAME_SIZE);
          } else if (dragging.role === 'bottom') {
            let y2 = Math.max(snapToGrid(pointer.y), bounds.y1 + MIN_FRAME_SIZE);
            const snap = findEdgeSnapValue(walls, ownIds, 'horizontal', y2, bounds.x1, bounds.x2, JOINT_SNAP_THRESHOLD);
            if (snap !== null) y2 += snap;
            bounds.y2 = Math.max(y2, bounds.y1 + MIN_FRAME_SIZE);
          } else if (dragging.role === 'left') {
            let x1 = Math.min(snapToGrid(pointer.x), bounds.x2 - MIN_FRAME_SIZE);
            const snap = findEdgeSnapValue(walls, ownIds, 'vertical', x1, bounds.y1, bounds.y2, JOINT_SNAP_THRESHOLD);
            if (snap !== null) x1 += snap;
            bounds.x1 = Math.min(x1, bounds.x2 - MIN_FRAME_SIZE);
          } else if (dragging.role === 'right') {
            let x2 = Math.max(snapToGrid(pointer.x), bounds.x1 + MIN_FRAME_SIZE);
            const snap = findEdgeSnapValue(walls, ownIds, 'vertical', x2, bounds.y1, bounds.y2, JOINT_SNAP_THRESHOLD);
            if (snap !== null) x2 += snap;
            bounds.x2 = Math.max(x2, bounds.x1 + MIN_FRAME_SIZE);
          }
          const wallsByRole = frameWallsFromBounds(group, bounds);
          setWalls((prev) => prev.map((w) => (wallsByRole[w.id] ? { ...w, ...wallsByRole[w.id] } : w)));
        }
        return;
      }
    }

if ((activeTool === 'wall' || activeTool === 'divider') && drawingStart) {
  const pointer = stage.getRelativePointerPosition();
  setPreviewPoint(pointer);
  setMagnifierPointer(pointer);
  setMagnifierConnected(false);
  setMagnifierJunction(null);
  const dist = Math.hypot(pointer.x - drawingStart.x, pointer.y - drawingStart.y);
  setMagnifierDistance(metersLabel(dist));
}
  };

const handleStagePointerUp = () => {
  if (paintStrokeRef.current) {
    finishPaintStroke();
    return;
  }
  if (zoneStroke) {
    finishZoneStroke();
    return;
  }
  if (!dragging) return;

  if (dragging.type === 'pan') {
    // موقعیت نهایی که در حین Pan مستقیم روی Konva اعمال شده بود (بدون setState) را
    // اینجا با state هماهنگ می‌کنیم؛ وگرنه dragging هیچ‌وقت پاک نمی‌شد و کل صفحه
    // برای همیشه در حالت "در حال درگ شدن" گیر می‌کرد
    setStagePos({ ...stagePosRef.current });
  } else if (dragging.type === 'bend') {
    pushHistory({ walls, doors, windows, openings, rooms });
  } else if (dragging.type === 'zoneEdge' || dragging.type === 'zoneCorner') {
    finishZoneDrag();
  } else if (dragging.type === 'wall') {
    const res = splitAtJunctions({ walls, doors, windows, openings }, [dragging.id]);
    setWalls(res.walls);
    setDoors(res.doors);
    setWindows(res.windows);
    setOpenings(res.openings);
    pushHistory({ walls: res.walls, doors: res.doors, windows: res.windows, openings: res.openings, rooms });
  } else if (
    dragging.type === 'door' ||
    dragging.type === 'window' ||
    dragging.type === 'opening' ||
    dragging.type === 'group-move' ||
    dragging.type === 'group-resize'
  ) {
    // پایان جابه‌جایی/تغییر دیوار، در/پنجره/بازشو یا گروه فضای آماده؛ برای این‌که Undo هم روی این تغییرات کار کند
    pushHistory({ walls, doors, windows, openings, rooms });
  }

  setDragging(null);
};

  // «خم کردنِ» دیوار/مرز از دستگیره‌ی وسط
  const handleBendStart = (e, wall) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    const hasItems =
      doors.some((d) => d.wallId === wall.id) || windows.some((d) => d.wallId === wall.id) || openings.some((d) => d.wallId === wall.id);
    if (hasItems) {
      showPlanMessage('روی این دیوار در/پنجره/بازشو هست — اول آن‌ها را بردارید تا دیوار منحنی شود');
      return;
    }
    setSelected({ type: 'wall', id: wall.id });
    setDragging({ type: 'bend', id: wall.id });
  };

  const handleWallMoveStart = (e, wall) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    const pointer = stageRef.current.getRelativePointerPosition();
    // تشخیص ناحیه لمس‌شده از همان لمس اول: سر/ته دیوار (برای وصل کردن، تغییر زاویه یا طول)
    // در برابر بدنه وسط (برای جابه‌جایی کل دیوار) — بدون نیاز به انتخاب قبلی
    const { ratio } = pointToSegment(pointer.x, pointer.y, wall.x1, wall.y1, wall.x2, wall.y2);
    const endZoneRatio = getWallEndZoneRatio(wall, scale);

    setSelected({ type: 'wall', id: wall.id });

    if (ratio <= endZoneRatio) {
      setDragging({ type: 'wall', id: wall.id, mode: 'start' });
    } else if (ratio >= 1 - endZoneRatio) {
      setDragging({ type: 'wall', id: wall.id, mode: 'end' });
    } else {
      setDragging({ type: 'wall', id: wall.id, mode: 'move', startPointer: pointer, original: { ...wall } });
    }
  };

  const handleWallHandleDown = (e, wall, endpoint) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    setSelected({ type: 'wall', id: wall.id });
    setDragging({ type: 'wall', id: wall.id, mode: endpoint });
  };

  // لمس داخل ناحیه یک گروه فضای آماده (نه روی خودِ یکی از چهار ضلعش) — جابه‌جایی کل گروه (رفتار دائمی)
  const handleFrameInteriorDown = (e, group) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    const bounds = getGroupBounds(walls, group);
    if (!bounds) return;
    const pointer = stageRef.current.getRelativePointerPosition();
    setDragging({ type: 'group-move', groupId: group.id, startPointer: pointer, originalBounds: bounds });
  };

  // لمس دقیقاً روی یکی از چهار ضلع یک گروه فضای آماده — کشیدن همان ضلع و تغییر مساحت فضا؛
  // این ضلع هرگز اجازه جدا شدن (کشیدن آزاد نوک/سر و ته) ندارد — همیشه فقط به‌عنوان یک ضلع از گروه تغییر اندازه می‌دهد
  const handleFrameEdgeDown = (e, group, role) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    setSelected({ type: 'wall', id: group[`${role}Id`] });
    setDragging({ type: 'group-resize', groupId: group.id, role });
  };

  const handleDoorWindowDown = (e, type, id, wallId) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    setSelected({ type, id });
    setDragging({ type, id, wallId });
  };

  // جهت بازشدن لنگه‌ی در (رو به بیرون / رو به داخل) — فقط برای در انتخاب‌شده قابل تغییر است
  // سمتِ لولا (در از کدام طرف باز شود) — آینه‌ی در در طولِ خودش
  const handleFlipDoorHinge = () => {
    if (!selected || selected.type !== 'door') return;
    const newDoors = doors.map((d) => (d.id === selected.id ? { ...d, hinge: d.hinge === 'end' ? 'start' : 'end' } : d));
    setDoors(newDoors);
    pushHistory({ walls, doors: newDoors, windows, openings, rooms });
  };

  const handleSetDoorFacing = (facing) => {
    if (!selected || selected.type !== 'door') return;
    const newDoors = doors.map((d) => (d.id === selected.id ? { ...d, facing } : d));
    setDoors(newDoors);
    pushHistory({ walls, doors: newDoors, windows, openings, rooms });
  };

  // ادیت ظاهرِ یک دیوار از داخل حالت سه‌بعدی (رنگ/براقیت/قرنیز) — patch فقط فیلدهای تغییریافته را
  // دارد (مثلاً { color } یا { baseboard })، بقیه‌ی فیلدهای دیوار دست‌نخورده می‌مانند. این تغییرات
  // همین‌جا در همان state اصلیِ walls ذخیره می‌شوند تا با برگشت به دو‌بعدی یا خروج/ورود دوباره از
  // بین نروند، و در تاریخچه (Undo/Redo) هم ثبت می‌شوند مثل بقیه‌ی تغییرات
  const handleUpdateWall = (wallIdIn, patch) => {
    // تکه‌ی دیوارِ منحنی در سه‌بعدی («12~3») → خودِ دیوار
    const wallId = parentWallId(wallIdIn);
    const newWalls = walls.map((w) => (w.id === wallId ? { ...w, ...patch } : w));
    setWalls(newWalls);
    pushHistory({ walls: newWalls, doors, windows, openings, rooms });
  };

  // چند دیوار با هم (مثلاً «همین پوشش برای همه‌ی دیوارهای این اتاق») — یک قدمِ واحد در تاریخچه
  const handleUpdateWalls = (patchesIn) => {
    const patchesById = {};
    Object.entries(patchesIn).forEach(([id, patch]) => {
      const pid = parentWallId(id);
      patchesById[pid] = { ...(patchesById[pid] || {}), ...patch };
    });
    const newWalls = walls.map((w) => (patchesById[w.id] ? { ...w, ...patchesById[w.id] } : w));
    setWalls(newWalls);
    pushHistory({ walls: newWalls, doors, windows, openings, rooms });
  };

  // ادیت ظاهر/ابعادِ یک در از داخل حالت سه‌بعدی (نوع/رنگ/دستگیره/عرض/ارتفاع) — دقیقاً همان الگوی
  // handleUpdateWall بالا، فقط روی state دیگرِ doors. مثلِ آن، در تاریخچه (Undo/Redo) هم ثبت می‌شود
  // چون این‌ها تصمیم‌های طراحی‌اند، برخلافِ «باز/بسته بودنِ» موقتیِ در در حالت اول‌شخص که اصلاً به
  // این‌جا نمی‌رسد و فقط داخل خودِ Create3D نگه داشته می‌شود
  const handleUpdateDoor = (doorId, patch) => {
    const newDoors = doors.map((d) => (d.id === doorId ? { ...d, ...patch } : d));
    setDoors(newDoors);
    pushHistory({ walls, doors: newDoors, windows, openings, rooms });
  };

  // ادیت ظاهر/ابعادِ یک پنجره از داخل حالت سه‌بعدی — دقیقاً همان الگوی handleUpdateDoor بالا، فقط
  // روی state دیگرِ windows
  const handleUpdateWindow = (windowId, patch) => {
    const newWindows = windows.map((w) => (w.id === windowId ? { ...w, ...patch } : w));
    setWindows(newWindows);
    pushHistory({ walls, doors, windows: newWindows, openings, rooms });
  };

  // ادیت عرض/ارتفاع/چارچوبِ یک بازشو از داخل حالت سه‌بعدی — مثلِ در و پنجره، در تاریخچه هم ثبت می‌شود
  const handleUpdateOpening = (openingId, patch) => {
    const newOpenings = openings.map((o) => (o.id === openingId ? { ...o, ...patch } : o));
    setOpenings(newOpenings);
    pushHistory({ walls, doors, windows, openings: newOpenings, rooms });
  };

  // ادیت جنس/رنگِ کفِ یک «گروهِ اتاق» از داخل حالت سه‌بعدی — برخلافِ سه هندلرِ بالا، اینجا روی
  // roomGroups کار می‌کند نه یکی از فیلدهای پلانِ ذخیره‌شونده در history؛ چون roomGroups خودش هم از
  // قبل بیرون از سیستمِ Undo/Redo مدیریت می‌شود (فقط شناسه‌ها را نگه می‌دارد و با هر پرش در تاریخچه
  // از روی walls دوباره زنده می‌شود، بالاتر در applyHistorySnapshot)، رنگ/جنسِ کف هم همان‌جا کنارِ
  // بقیه‌ی خصوصیاتِ گروه (label و غیره) ذخیره می‌شود و مشمولِ Undo/Redo نیست
  const handleUpdateRoomFloor = (groupId, patch) => updateSpace(groupId, patch);

  // «اتاقِ آماده» → روی خودِ roomGroups؛ فضای دستی → در freeSpaces (اگر نبود، با seedِ همان تکه اضافه می‌شود)
  const spacesRef = useRef([]);
  const updateSpace = (spaceId, patch) => {
    if (roomGroups.some((g) => g.id === spaceId)) {
      setRoomGroups((prev) => prev.map((g) => (g.id === spaceId ? { ...g, ...patch } : g)));
      return;
    }
    const sp = spacesRef.current.find((r) => r.id === spaceId);
    setFreeSpaces((prev) => {
      if (prev.some((f) => f.id === spaceId)) return prev.map((f) => (f.id === spaceId ? { ...f, ...patch } : f));
      if (!sp) return prev;
      return [...prev, { id: spaceId, seed: sp.seed, label: sp.label, ...patch }];
    });
  };

  // دقیقاً همان الگوی handleUpdateRoomFloor برای سقف — همان‌جا کنارِ floorStyle روی خودِ roomGroups
  // ذخیره می‌شود، بیرون از سیستمِ Undo/Redo
  const handleUpdateRoomCeiling = (groupId, patch) => updateSpace(groupId, patch);

  const handleRoomPointerDown = (e, room) => {
    if (activeTool !== 'select') return;
    e.cancelBubble = true;
    if (selected?.type === 'room' && selected.id === room.id) {
      const stage = stageRef.current;
      const screenPos = worldToScreen(stage, room.centroid);
      setPendingRoom({ ...room, renamingId: room.id, screenPos });
    } else {
      setSelected({ type: 'room', id: room.id });
    }
  };

  // area: مساحتی که کاربر نوشت (متر مربع) یا null (= تخمینِ برنامه)
  const handleConfirmRoomName = (name, area) => {
    const lockInfo = area ? { areaLocked: true, area } : { areaLocked: false, area: null };
    let roomId;
    let newRooms;
    if (pendingRoom.renamingId) {
      roomId = pendingRoom.renamingId;
      newRooms = rooms.map((r) => (r.id === roomId ? { ...r, name, ...lockInfo } : r));
    } else {
      roomId = nextRoomId.current++;
      newRooms = [
        ...rooms,
        {
          id: roomId,
          rowSpans: pendingRoom.rowSpans,
          cellSize: pendingRoom.cellSize,
          centroid: pendingRoom.centroid,
          areaSqm: pendingRoom.areaSqm,
          name,
          ...lockInfo,
        },
      ];
      setActiveTool('select');
    }
    setPendingRoom(null);
    setSelected({ type: 'room', id: roomId });
    const room = newRooms.find((r) => r.id === roomId);
    const current = room ? roomAreaOf(room) : 0;
    if (area && Math.abs(current - area) > 0.05) {
      if (applyRoomArea(newRooms, roomId, area)) return;
      // ممکن نشد — اسم ذخیره می‌شود، مساحت نه
      const fallback = newRooms.map((r) => (r.id === roomId ? { ...r, areaLocked: false, area: null } : r));
      setRooms(fallback);
      pushHistory({ walls, doors, windows, openings, rooms: fallback });
      showPlanMessage('این مساحت با مساحتِ ثابتِ بقیه‌ی اتاق‌ها جور درنمی‌آید — مساحتِ یکی از آن‌ها را خالی کنید');
      return;
    }
    setRooms(newRooms);
    pushHistory({ walls, doors, windows, openings, rooms: newRooms });
  };

  const handleDeleteSelected = () => {
    if (!selected) return;

    if (selected.type === 'wall') {
      const newWalls = walls.filter((w) => w.id !== selected.id);
      const newDoors = doors.filter((d) => d.wallId !== selected.id);
      const newWindows = windows.filter((w) => w.wallId !== selected.id);
      const newOpenings = openings.filter((o) => o.wallId !== selected.id);
      setWalls(newWalls);
      setDoors(newDoors);
      setWindows(newWindows);
      setOpenings(newOpenings);
      pushHistory({ walls: newWalls, doors: newDoors, windows: newWindows, openings: newOpenings, rooms });
    } else if (selected.type === 'door') {
      const newDoors = doors.filter((d) => d.id !== selected.id);
      setDoors(newDoors);
      pushHistory({ walls, doors: newDoors, windows, openings, rooms });
    } else if (selected.type === 'window') {
      const newWindows = windows.filter((w) => w.id !== selected.id);
      setWindows(newWindows);
      pushHistory({ walls, doors, windows: newWindows, openings, rooms });
    } else if (selected.type === 'opening') {
      const newOpenings = openings.filter((o) => o.id !== selected.id);
      setOpenings(newOpenings);
      pushHistory({ walls, doors, windows, openings: newOpenings, rooms });
    } else if (selected.type === 'entrance') {
      setEntrance(null);
    } else if (selected.type === 'room') {
      const newRooms = rooms.filter((r) => r.id !== selected.id);
      // «قسمت»: مرزهایش هم با خودش پاک می‌شود
      const newWalls = walls.some((w) => w.zoneOf === selected.id) ? walls.filter((w) => w.zoneOf !== selected.id) : walls;
      setWalls(newWalls);
      setRooms(newRooms);
      setZoneEdit(null);
      pushHistory({ walls: newWalls, doors, windows, openings, rooms: newRooms });
    }
    setSelected(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected && !pendingRoom) {
        handleDeleteSelected();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selected, walls, doors, windows, openings, rooms, pendingRoom]);

  const applyHistorySnapshot = (snapshot) => {
    setWalls(snapshot.walls);
    setDoors(snapshot.doors);
    setWindows(snapshot.windows);
    setOpenings(snapshot.openings || []);
    setRooms(snapshot.rooms);
    setSelected(null);
    // roomGroups در تاریخچه ذخیره نمی‌شود چون فقط شناسه‌ها را نگه می‌دارد، نه مختصات؛ با پرش در تاریخچه،
    // محدوده هر گروه دوباره زنده از روی walls جدید محاسبه می‌شود (و اگر دیوارهایش نباشند، خودبه‌خود غیرفعال می‌شود)
  };

  const handleUndo = () => {
    if (historyIndex === 0) return;
    setZoneEdit(null);
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    applyHistorySnapshot(history[newIndex]);
  };

  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;
    setZoneEdit(null);
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    applyHistorySnapshot(history[newIndex]);
  };

const handleToolChange = (tool) => {
  setActiveTool(tool);
  // «مرزِ فضا» = مرزبندیِ هوشمند (اول اسم)؛ «خودم می‌کشم» = کشیدنِ دستیِ قبلی
  setZoneForm(false);
  closeZonePaint();
  if (tool === 'divider') startZonePaint();
  setZoneDraw(null);
  setZoneStroke(null);
  setZoneSide(null);
  setZoneEdit(null);
  setDrawingStart(null);
  setPreviewPoint(null);
  setSelected(null);
  setDragging(null);
  setPendingRoom(null);
  setMagnifierPointer(null);
  setMagnifierAngle(null);
  setMagnifierDistance(null);
  setMagnifierConnected(false);
  setMagnifierJunction(null);
};

  const handleZoomIn = () => animateScaleTo(scale * ZOOM_STEP_FACTOR);
  const handleZoomOut = () => animateScaleTo(scale / ZOOM_STEP_FACTOR);

  // همه‌ی «فضاها» برای مدل سه‌بعدی: هر بخشِ بسته (اتاقِ آماده یا دیوارکشیِ دستی) با کف، سقف، نور و پوششِ خودش؛
  // اتاقِ داخلِ اتاقِ بزرگ‌تر فضای جداست و اتاقِ بزرگ دورِ آن را دور می‌زند. فقط وقتی سه‌بعدی باز است حساب می‌شود
  const roomGroupRects = useMemo(() => {
    if (!view3D) return [];
    const groups = roomGroups
      .map((group) => {
        const bounds = getGroupBounds(walls, group);
        if (!bounds) return null;
        return {
          id: group.id,
          label: group.label,
          bounds,
          floorStyle: group.floorStyle,
          ceilingStyle: group.ceilingStyle,
          // «همین پوشش برای همه‌ی دیوارهای این اتاق؟» یک بار پرسیده شد ('applied' | 'dismissed')
          coverPrompt: group.coverPrompt,
          // شناسه‌ی چهار دیوارِ این اتاق — بینِ دو دیوارِ رویِ‌همِ یک دیوارِ مشترک، دیوارِ همین اتاق
          wallIds: [group.topId, group.rightId, group.bottomId, group.leftId],
          // ضلعِ منحنی: کف/سقف از شکلِ واقعی (نه مستطیلِ ساده)
          curved: [group.topId, group.rightId, group.bottomId, group.leftId].some((id) => isArc(walls.find((w) => w.id === id))),
        };
      })
      .filter(Boolean);
    return computeSpaces(walls, groups, freeSpaces, rooms);
  }, [view3D, walls, roomGroups, freeSpaces, rooms]);
  spacesRef.current = roomGroupRects;

  // «پاک کردنِ کلِ پلان» (با تأیید) — با «واگرد» برمی‌گردد
  const handleClearPlan = () => {
    setWalls([]);
    setDoors([]);
    setWindows([]);
    setOpenings([]);
    setRooms([]);
    setRoomGroups([]);
    setFreeSpaces([]);
    setEntrance(null);
    setSelected(null);
    setDrawingStart(null);
    setPreviewPoint(null);
    setPendingRoom(null);
    setWallAnim(null);
    setGuideRect({ x: -guideSidePx / 2, y: -guideSidePx / 2, w: guideSidePx, h: guideSidePx });
    pushHistory({ walls: [], doors: [], windows: [], openings: [], rooms: [] });
    setActiveTool('wall');
    setConfirmClear(false);
  };

  // سه‌بعدی فقط دیوارهای واقعی را می‌سازد؛ «مرزِ فضا» فقط در roomGroupRects (کف/سقفِ جدا) اثر دارد
  // useMemo: آرایه‌ی تازه در هر رندر = محاسبه‌ی دوباره‌ی کلِ صحنه‌ی سه‌بعدی (لگ)
  const realWalls = useMemo(() => expandArcs(walls.some((w) => w.virtual) ? walls.filter((w) => !w.virtual) : walls), [walls]);

  if (view3D) {
    return (
      <Create3D
        walls={realWalls}
        doors={doors}
        windows={windows}
        openings={openings}
        roomGroupRects={roomGroupRects}
        entrance={entrance}
        site={site}
        wallThickness={WALL_THICKNESS}
        doorWidth={DOOR_WIDTH}
        windowWidth={WINDOW_WIDTH}
        openingWidth={OPENING_WIDTH}
        onBack={() => setView3D(false)}
        onUpdateWall={handleUpdateWall}
        onUpdateWalls={handleUpdateWalls}
        onUpdateDoor={handleUpdateDoor}
        onUpdateWindow={handleUpdateWindow}
        onUpdateOpening={handleUpdateOpening}
        onUpdateRoomFloor={handleUpdateRoomFloor}
        onUpdateRoomCeiling={handleUpdateRoomCeiling}
        planSnapshot={{ walls, doors, windows, openings, rooms, roomGroups, freeSpaces, entrance, propertyArea: propertyArea || null }}
        onFinishDesign={
          onFinish
            ? (prepared) =>
                onFinish({ ...prepared, editorState: { walls, doors, windows, openings, rooms, roomGroups, freeSpaces, guide: guideRect, entrance } })
            : null
        }
        listing={listing}
      />
    );
  }

  const getHintText = () => {
    if (roomError) return roomError;
    if (activeTool === 'wall') return drawingStart ? 'نقطه پایان دیوار را انتخاب کنید' : 'برای شروع دیوار، روی صفحه کلیک کنید';
    // راهنمای «رنگ کردنِ خانه‌ها» داخلِ نوارِ خودش است
    if (activeTool === 'divider' && zonePaint) return roomError || null;
    if (activeTool === 'divider')
      return drawingStart
        ? 'نقطه‌ی پایانِ مرز را انتخاب کنید'
        : zoneSide
          ? `کدام طرف «${zoneSide.name}» است؟ روی همان طرف بزنید`
          : zoneDraw
            ? `مرزِ «${zoneDraw.name}» را با انگشت بکشید — از یک دیوار تا دیوارِ دیگر؛ صاف بودنش مهم نیست`
            : 'مرزِ فضا: جایی که دیوار نیست ولی دو فضا جدا هستند (مثلاً پذیرایی و آشپزخانه) — نقطه‌ی شروع را بزنید';
    if (activeTool === 'door') return 'روی یک دیوار کلیک کنید تا در قرار بگیرد';
    if (activeTool === 'window') return 'روی یک دیوار کلیک کنید تا پنجره قرار بگیرد';
    if (activeTool === 'opening') return 'روی دیوار مشترک دو فضا کلیک کنید تا آن تکه باز شود (بدون دیوار)';
    if (activeTool === 'room') return 'داخل یک فضای بسته کلیک کنید تا اتاق تشخیص داده شود';
    if (activeTool === 'select' && selected?.type === 'wall') {
      const groupInfo = findWallGroupRole(roomGroups, selected.id);
      if (groupInfo && getGroupBounds(walls, groupInfo.group)) {
        if (dragging && dragging.type === 'bend') return 'برای صاف شدن، دایره را به وسطِ خط برگردانید';
        return 'این ضلع از یک فضای آماده است — بکشیدش تا اندازه‌ی فضا عوض شود؛ برای منحنی کردن، دایره‌ی نارنجیِ وسطش را بکشید';
      }
      if (dragging && dragging.type === 'bend') return 'برای صاف شدن، دایره را به وسطِ خط برگردانید';
      return 'برای منحنی کردن، دایره‌ی نارنجیِ وسطِ دیوار را بکشید';
    }
    if (activeTool === 'select' && selected) return 'برای جابه‌جایی درگ کنید، برای حذف دکمه سطل زباله یا Delete را بزنید';
    if (activeTool === 'select') return 'روی یک عنصر کلیک کنید، یا فضای خالی را برای جابه‌جایی نما بکشید';
    return null;
  };

  const hintText = getHintText();

  // پنل انتخاب جهت بازشدن در (رو به بیرون / رو به داخل) — هم بلافاصله بعد از جاگذاری یک در تازه
  // نمایش داده می‌شود (چون جاگذاری خودش selected را روی همان در تنظیم می‌کند)، هم با انتخاب دوباره‌ی
  // هر در موجود از قبل
  const selectedDoorPanel = (() => {
    if (selected?.type !== 'door') return null;
    const door = doors.find((d) => d.id === selected.id);
    if (!door) return null;
    const wall = walls.find((w) => w.id === door.wallId);
    if (!wall) return null;
    const stage = stageRef.current;
    if (!stage) return null;
    const doorW = door.style?.widthCm || DOOR_WIDTH;
    const placement = getItemPlacement(wall, door.ratio, doorW);
    const angleRad = (placement.angleDeg * Math.PI) / 180;
    const centerX = placement.x + Math.cos(angleRad) * (doorW / 2);
    const centerY = placement.y + Math.sin(angleRad) * (doorW / 2);
    const screenPos = worldToScreen(stage, { x: centerX, y: centerY });
    return { door, screenPos };
  })();

  return (
    <div className="create-plan-editor">
      <PlanToolbar
        activeTool={activeTool}
        onToolChange={handleToolChange}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onClose={handleClose}
        onClearPlan={() => setConfirmClear(true)}
        canClear={walls.length > 0 || doors.length > 0 || rooms.length > 0 || roomGroups.length > 0 || Boolean(entrance)}
        hasSelection={!!selected}
        onDelete={handleDeleteSelected}
        onOpen3D={() => setView3D(true)}
        onScan={() => setSketchOpen(true)}
      />

      <RoomPresetBar onSelect={handleCreateRoomPreset} />

      <div className="create-plan-editor__canvas">
<Stage
  ref={stageRef}
  width={window.innerWidth}
  height={window.innerHeight - CANVAS_TOP_OFFSET}
  scaleX={scale}
  scaleY={scale}
  x={stagePos.x}
  y={stagePos.y}
  onPointerDown={handleStagePointerDown}
  onPointerMove={handleStagePointerMove}
  onPointerUp={handleStagePointerUp}
>
          {/* موجِ «مرزبندیِ هوشمند» در لایه‌ی جدا — هر فریم فقط همین لایه دوباره کشیده می‌شود */}
          <Layer listening={false}>
            {zoneEditRoom && (
              <Shape
                ref={zoneWaveRef}
                listening={false}
                perfectDrawEnabled={false}
                sceneFunc={(ctx) => {
                  const c = ctx._context;
                  const poly = zonePolyOf(zoneEditRoom);
                  if (!poly || poly.length < 3) return;
                  const t = (performance.now() - zoneEdit.t0) / 1000;
                  const path = () => {
                    c.beginPath();
                    c.moveTo(poly[0][0], poly[0][1]);
                    for (let k = 1; k < poly.length; k += 1) c.lineTo(poly[k][0], poly[k][1]);
                    c.closePath();
                  };
                  c.save();
                  path();
                  c.fillStyle = 'rgba(31, 157, 85, 0.08)';
                  c.fill();
                  // یک موجِ نرم و آرام از وسطِ قسمت به طرفِ لبه‌ها
                  c.clip();
                  const ctr = polyCentroid(poly);
                  const maxR = Math.max(...poly.map(([x, y]) => Math.hypot(x - ctr.x, y - ctr.y))) || 1;
                  const ph = (t / 2.8) % 1;
                  const r = maxR * (0.15 + 0.95 * ph);
                  const a = 0.16 * Math.sin(Math.PI * ph);
                  const g = c.createRadialGradient(ctr.x, ctr.y, r * 0.55, ctr.x, ctr.y, r);
                  g.addColorStop(0, 'rgba(31, 157, 85, 0)');
                  g.addColorStop(0.85, `rgba(31, 157, 85, ${a})`);
                  g.addColorStop(1, 'rgba(31, 157, 85, 0)');
                  c.fillStyle = g;
                  c.fillRect(ctr.x - r, ctr.y - r, r * 2, r * 2);
                  c.restore();
                  // خط‌چینِ لبه‌های آزاد که آرام حرکت می‌کند (لبه‌های روی دیوار همان دیوارند)
                  const fl = dragging && dragging.roomId === zoneEditRoom.id ? dragging.flags0 : zoneFlags.get(zoneEditRoom.id) || [];
                  c.save();
                  c.beginPath();
                  poly.forEach((pa, k) => {
                    if (fl[k]) return;
                    const pb = poly[(k + 1) % poly.length];
                    c.moveTo(pa[0], pa[1]);
                    c.lineTo(pb[0], pb[1]);
                  });
                  c.strokeStyle = '#1f9d55';
                  c.lineWidth = 2.4 / scale;
                  c.setLineDash([10 / scale, 7 / scale]);
                  c.lineDashOffset = (-t * 22) / scale;
                  c.stroke();
                  c.restore();
                }}
              />
            )}

          </Layer>
          <Layer>
            <Shape
              sceneFunc={(ctx, shape) => {
                drawGrid(ctx);
                ctx.fillStrokeShape(shape);
              }}
              listening={false}
              perfectDrawEnabled={false}
            />

            {/* فضای راهنمای پیشنهادی، بر اساس متراژ ملک ثبت‌شده — صرفاً جنبه راهنما دارد */}
            <Rect
              x={guideRect.x}
              y={guideRect.y}
              width={guideRect.w}
              height={guideRect.h}
              fill="rgba(0, 0, 0, 0.045)"
              stroke="#c7c7c7"
              strokeWidth={1}
              dash={[6, 6]}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Text
              x={guideRect.x}
              y={guideRect.y - 22}
              width={guideRect.w}
              align="center"
              text={`فضای پیشنهادی: ${guideAreaSqm.toLocaleString('fa-IR')} متر مربع`}
              fontFamily="Vazirmatn, sans-serif"
              fontSize={12}
              fill="#8e8e8e"
              listening={false}
              perfectDrawEnabled={false}
            />

            {rooms.map((room) => (
              <Otagh
                key={room.id}
                rowSpans={room.rowSpans}
                cellSize={room.cellSize}
                centroid={room.centroid}
                name={room.name}
                areaSqm={roomAreaOf(room)}
                locked={Boolean(room.areaLocked)}
                selected={selected?.type === 'room' && selected.id === room.id}
                onPointerDown={(e) => handleRoomPointerDown(e, room)}
              />
            ))}

            {/* ناحیه داخلی + پس‌زمینه تیره ثابت هر فضای آماده (اتاق‌خواب، بالکن و ...) — همیشه نمایش داده می‌شود،
                نه فقط وقتی انتخاب شده. کمی از خودِ دیوارها فاصله (پدینگ) دارد تا دو فضای هم‌جوار (مثلاً دو
                اتاق‌خواب کنار هم که یک دیوار مشترک دارند) در تیرگی یکدیگر ادغام نشوند و از هم قابل تفکیک بمانند.
                زیر خطوط دیوارها رندر می‌شود تا لمس روی خودِ یک ضلع همچنان اولویت با تغییر اندازه همان ضلع باشد.
                لمس هر نقطه از این ناحیه، کل گروه را جابه‌جا می‌کند */}
            {roomGroups.map((group) => {
              const bounds = getGroupBounds(walls, group);
              if (!bounds) return null;
              const insetX1 = bounds.x1 + ROOM_LABEL_FILL_INSET;
              const insetY1 = bounds.y1 + ROOM_LABEL_FILL_INSET;
              const insetX2 = bounds.x2 - ROOM_LABEL_FILL_INSET;
              const insetY2 = bounds.y2 - ROOM_LABEL_FILL_INSET;
              const insetWidth = Math.max(0, insetX2 - insetX1);
              const insetHeight = Math.max(0, insetY2 - insetY1);
              // ضلعِ منحنی: قطعه‌ی دایره به مساحت اضافه (قوسِ رو به بیرون) یا از آن کم (رو به داخل) می‌شود — اضلاع
              // ساعت‌گرد چیده شده‌اند، پس خیزِ مثبت (سمتِ چپِ جهتِ دیوار) رو به داخل است
              let arcAreaCm2 = 0;
              [group.topId, group.rightId, group.bottomId, group.leftId].forEach((id) => {
                const w = walls.find((x) => x.id === id);
                if (!isArc(w)) return;
                const g = arcGeom(w);
                if (g.straight) return;
                const th = Math.abs(g.sweep);
                arcAreaCm2 += -Math.sign(g.h) * ((g.R * g.R) / 2) * (th - Math.sin(th));
              });
              const areaSqm = ((bounds.x2 - bounds.x1) * (bounds.y2 - bounds.y1) + arcAreaCm2) / 10000;

              // اندازه واقعی چهارچوب روی صفحه (پیکسل، نه واحد جهانی) — مبنای تصمیم برای اندازه فونت و
              // این‌که اصلاً جا برای اسم/مساحت هست یا نه؛ با این حساب متن هیچ‌وقت از داخل باکس بیرون نمی‌زند
              const boxWidthPx = insetWidth * scale;
              const boxHeightPx = insetHeight * scale;
              const minSidePx = Math.min(boxWidthPx, boxHeightPx);

              const showLabel = minSidePx >= ROOM_LABEL_VISIBLE_MIN_PX;
              const showArea = showLabel && boxHeightPx >= ROOM_AREA_VISIBLE_MIN_PX;

              // فونت اسم فضا: هرچه چهارچوب کوچیک‌تر، فونت هم کوچیک‌تر می‌شود؛ اگر باز هم جا نشد (اسم بلند
              // در چهارچوب باریک)، به‌جای بیرون‌زدن، با «...» کوتاه می‌شود (wrap="none" + ellipsis پایین‌تر)
              const nameFontPx = Math.max(
                ROOM_LABEL_MIN_FONT_PX,
                Math.min(ROOM_LABEL_MAX_FONT_PX, boxHeightPx * 0.22, boxWidthPx * 0.15)
              );
              const areaFontPx = Math.max(ROOM_AREA_MIN_FONT_PX, Math.min(ROOM_AREA_MAX_FONT_PX, nameFontPx - 2));
              const fontRatio = nameFontPx / ROOM_LABEL_MAX_FONT_PX;

              return (
                // Group با clip دقیقاً هم‌اندازه ناحیه تیره — یک ضمانت قطعی که هیچ متنی، حتی در حالت‌های حدی،
                // هرگز از مرز این باکس بیرون رندر نشود (علاوه بر کوچیک‌شدن فونت و کوتاه‌شدن با «...»)
                <Group
                  key={`group-${group.id}`}
                  clipX={insetX1}
                  clipY={insetY1}
                  clipWidth={insetWidth}
                  clipHeight={insetHeight}
                >
                  <Rect
                    x={insetX1}
                    y={insetY1}
                    width={insetWidth}
                    height={insetHeight}
                    fill="rgba(20, 20, 20, 0.09)"
                    cornerRadius={4}
                    perfectDrawEnabled={false}
                    onPointerDown={(e) => handleFrameInteriorDown(e, group)}
                  />
                  {showLabel && (
                    <Text
                      x={insetX1}
                      y={(bounds.y1 + bounds.y2) / 2 - (showArea ? 20 * fontRatio : nameFontPx * 0.6) / scale}
                      width={insetWidth}
                      align="center"
                      text={group.label}
                      fontFamily="Vazirmatn, sans-serif"
                      fontSize={nameFontPx / scale}
                      fontStyle="700"
                      fill="#333333"
                      wrap="none"
                      ellipsis
                      listening={false}
                      perfectDrawEnabled={false}
                    />
                  )}
                  {showArea && (
                    <Text
                      x={insetX1}
                      y={(bounds.y1 + bounds.y2) / 2 + (2 * fontRatio) / scale}
                      width={insetWidth}
                      align="center"
                      text={`${areaSqm.toLocaleString('fa-IR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} متر مربع`}
                      fontFamily="Vazirmatn, sans-serif"
                      fontSize={areaFontPx / scale}
                      fill="#5a5a5a"
                      wrap="none"
                      ellipsis
                      listening={false}
                      perfectDrawEnabled={false}
                    />
                  )}
                </Group>
              );
            })}

            {wallAnim &&
              walls.map((wall) => {
                if (wall.zoneOf != null) return null;
                const span = wallAnim.plan.get(wall.id);
                const k = span ? Math.max(0, Math.min(1, (animNow - span[0]) / Math.max(0.01, span[1] - span[0]))) : 1;
                if (k <= 0) return null;
                const e = 1 - (1 - k) * (1 - k); // آرام در انتها
                const rev = span && span[2];
                const ax = rev ? wall.x2 : wall.x1;
                const ay = rev ? wall.y2 : wall.y1;
                const bx = rev ? wall.x1 : wall.x2;
                const by = rev ? wall.y1 : wall.y2;
                let x2 = ax + (bx - ax) * e;
                let y2 = ay + (by - ay) * e;
                let pts = [ax, ay, x2, y2];
                if (isArc(wall)) {
                  // دیوارِ منحنی: قلم روی خودِ قوس جلو می‌رود
                  const ap = arcPoints(wall, 12);
                  if (rev) ap.reverse();
                  const n = ap.length - 1;
                  const f = e * n;
                  const i = Math.min(n - 1, Math.floor(f));
                  const tt = f - i;
                  x2 = ap[i].x + (ap[i + 1].x - ap[i].x) * tt;
                  y2 = ap[i].y + (ap[i + 1].y - ap[i].y) * tt;
                  pts = ap.slice(0, i + 1).flatMap((p) => [p.x, p.y]).concat([x2, y2]);
                }
                return (
                  <React.Fragment key={`anim-${wall.id}`}>
                    <Line
                      points={pts}
                      stroke={wall.virtual ? '#5b8fd6' : '#000000'}
                      strokeWidth={wall.virtual ? 3 : WALL_THICKNESS}
                      dash={wall.virtual ? [12, 8] : undefined}
                      lineCap={wall.virtual ? 'round' : 'square'}
                      listening={false}
                      perfectDrawEnabled={false}
                    />
                    {k < 1 && <Circle x={x2} y={y2} radius={7 / scale + WALL_THICKNESS / 2} fill="#0a66c2" listening={false} />}
                  </React.Fragment>
                );
              })}

            {!wallAnim && walls.map((wall) => {
              // مرزهای «قسمت» جدا کشیده نمی‌شوند — خودِ خط‌چینِ بسته‌ی قسمت (پایین‌تر) کشیده و کشیده می‌شود
              if (wall.zoneOf != null) return null;
              const isSelected = selected?.type === 'wall' && selected.id === wall.id;
              const groupInfo = findWallGroupRole(roomGroups, wall.id);
              const groupBounds = groupInfo ? getGroupBounds(walls, groupInfo.group) : null;

              if (groupInfo && groupBounds) {
                // ضلعی از یک گروه فضای آماده — این محدودیت دائمی است و هرگز «نهایی» نمی‌شود:
                // بدون دستگیره سر/ته (هرگز حق جدا شدن ندارد)، فقط کشیدن کل ضلع (تغییر اندازه گروه) یا
                // لمس داخل فضا (جابه‌جایی کل گروه) ممکن است. رنگ مثل یک دیوار معمولی (آبی هنگام انتخاب)
                // چون همچنان یک دیوار واقعی است و در/پنجره می‌پذیرد
                // مثلِ دیوارِ ساده منحنی هم می‌شود: دستگیره‌ی نارنجیِ وسط (وقتی انتخاب شده)
                return (
                  <React.Fragment key={wall.id}>
                    <Line
                      points={isArc(wall) ? arcFlat(wall) : [wall.x1, wall.y1, wall.x2, wall.y2]}
                      stroke={isSelected ? '#0a66c2' : '#000000'}
                      strokeWidth={isSelected ? WALL_THICKNESS + 2 : WALL_THICKNESS}
                      lineCap="square"
                      lineJoin="round"
                      hitStrokeWidth={28}
                      perfectDrawEnabled={false}
                      onPointerDown={(e) => handleFrameEdgeDown(e, groupInfo.group, groupInfo.role)}
                    />
                    {isSelected &&
                      (() => {
                        const g = arcGeom(wall);
                        return (
                          <Group x={g.apex.x} y={g.apex.y} onPointerDown={(e) => handleBendStart(e, wall)}>
                            <Circle radius={22 / scale} fill="transparent" />
                            <Circle radius={9 / scale} fill="#ffffff" stroke="#e8590c" strokeWidth={2.5 / scale} />
                            <Circle radius={3.2 / scale} fill="#e8590c" listening={false} />
                          </Group>
                        );
                      })()}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={wall.id}>
                  <Line
                    points={isArc(wall) ? arcFlat(wall) : [wall.x1, wall.y1, wall.x2, wall.y2]}
                    stroke={wall.virtual ? (isSelected ? '#0a66c2' : '#5b8fd6') : isSelected ? '#0a66c2' : '#000000'}
                    strokeWidth={wall.virtual ? (isSelected ? 4 : 3) : isSelected ? WALL_THICKNESS + 2 : WALL_THICKNESS}
                    dash={wall.virtual ? [12, 8] : undefined}
                    lineCap={wall.virtual ? 'round' : 'square'}
                    hitStrokeWidth={24}
                    perfectDrawEnabled={false}
                    onPointerDown={(e) => handleWallMoveStart(e, wall)}
                  />
                  {/* ناحیه‌های لمسی نامرئی سر و ته دیوار — همیشه فعالند (چه دیوار انتخاب شده باشد چه نه)
                      تا همان لمس اول برای گرفتن سر/ته دیوار کافی باشد، نه فقط بعد از یک بار انتخاب قبلی */}
                  <Circle
                    x={wall.x1}
                    y={wall.y1}
                    radius={getWallEndZoneDistance(wall, scale)}
                    fill="transparent"
                    onPointerDown={(e) => handleWallHandleDown(e, wall, 'start')}
                  />
                  <Circle
                    x={wall.x2}
                    y={wall.y2}
                    radius={getWallEndZoneDistance(wall, scale)}
                    fill="transparent"
                    onPointerDown={(e) => handleWallHandleDown(e, wall, 'end')}
                  />
                  {isSelected &&
                    (() => {
                      // دستگیره‌ی «خم کردن»: وسطِ دیوار — بکشید تا منحنی شود؛ برگرداندن به وسط = دوباره صاف
                      const g = arcGeom(wall);
                      return (
                        <Group
                          x={g.apex.x}
                          y={g.apex.y}
                          onPointerDown={(e) => handleBendStart(e, wall)}
                        >
                          <Circle radius={22 / scale} fill="transparent" />
                          <Circle radius={9 / scale} fill="#ffffff" stroke="#e8590c" strokeWidth={2.5 / scale} />
                          <Circle radius={3.2 / scale} fill="#e8590c" listening={false} />
                        </Group>
                      );
                    })()}
                  {isSelected && (
                    <>
                      <Circle
                        x={wall.x1}
                        y={wall.y1}
                        radius={WALL_HANDLE_VISUAL_RADIUS_PX / scale}
                        fill="#ffffff"
                        stroke="#0a66c2"
                        strokeWidth={2 / scale}
                        listening={false}
                      />
                      <Circle
                        x={wall.x2}
                        y={wall.y2}
                        radius={WALL_HANDLE_VISUAL_RADIUS_PX / scale}
                        fill="#ffffff"
                        stroke="#0a66c2"
                        strokeWidth={2 / scale}
                        listening={false}
                      />
                    </>
                  )}
                </React.Fragment>
              );
            })}

            {/* «قسمت»ها: فقط لبه‌های آزاد (روی دیوار نیستند) خط‌چین کشیده می‌شوند؛ لمس = ویرایش */}
            {!wallAnim &&
              !zonePaint &&
              rooms.map((room) => {
                if (!room.zone || !room.zone.poly || room.zone.poly.length < 3) return null;
                if (zoneEditRoom && zoneEditRoom.id === room.id) return null;
                const poly = room.zone.poly;
                const flags = zoneFlags.get(room.id) || [];
                const k = 1 / Math.max(scale, 0.3);
                return poly.map((a, i) => {
                  if (flags[i]) return null;
                  const b = poly[(i + 1) % poly.length];
                  return (
                    <Line
                      key={`zone-${room.id}-${i}`}
                      points={[a[0], a[1], b[0], b[1]]}
                      stroke="#5b8fd6"
                      strokeWidth={2.5 * k}
                      dash={[12 * k, 8 * k]}
                      lineCap="round"
                      hitStrokeWidth={30 * k}
                      listening={activeTool === 'select'}
                      perfectDrawEnabled={false}
                      onPointerDown={(e) => {
                        e.cancelBubble = true;
                        setSelected({ type: 'room', id: room.id });
                      }}
                    />
                  );
                });
              })}

            {/* دستگیره‌های قسمتِ در حالِ ویرایش: ↔ وسطِ هر لبه‌ی آزاد، ● گوشه‌ها، + برای گوشه‌ی تازه */}
            {!wallAnim &&
              zoneEditRoom &&
              (() => {
                const room = zoneEditRoom;
                const poly = zonePolyOf(room);
                const flags = dragging && dragging.roomId === room.id ? dragging.flags0 : zoneFlags.get(room.id) || [];
                const N = poly.length;
                const k = 1 / scale;
                const out = [];
                poly.forEach((a, i) => {
                  if (flags[i]) return;
                  const b = poly[(i + 1) % N];
                  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
                  if (len * scale < 24) return;
                  const mx = (a[0] + b[0]) / 2;
                  const my = (a[1] + b[1]) / 2;
                  const nx = -(b[1] - a[1]) / (len || 1);
                  const ny = (b[0] - a[0]) / (len || 1);
                  out.push(
                    <Group key={`ze-${i}`} onPointerDown={(e) => startZoneHandle(e, room, 'zoneEdge', i)}>
                      <Circle x={mx} y={my} radius={22 * k} fill="rgba(0,0,0,0.001)" />
                      <Circle x={mx} y={my} radius={11 * k} fill="#ffffff" stroke="#1f9d55" strokeWidth={2 * k} listening={false} />
                      <Line
                        points={[mx - nx * 6 * k, my - ny * 6 * k, mx + nx * 6 * k, my + ny * 6 * k]}
                        stroke="#1f9d55"
                        strokeWidth={2 * k}
                        lineCap="round"
                        listening={false}
                      />
                      {[1, -1].map((sg) => {
                        // سرِ فلش در دو طرفِ عمود
                        const ex = mx + nx * 6 * k * sg;
                        const ey = my + ny * 6 * k * sg;
                        const bx = ex - nx * 3 * k * sg;
                        const by = ey - ny * 3 * k * sg;
                        const tx = (b[0] - a[0]) / (len || 1);
                        const ty = (b[1] - a[1]) / (len || 1);
                        return (
                          <Line
                            key={sg}
                            points={[bx + tx * 3 * k, by + ty * 3 * k, ex, ey, bx - tx * 3 * k, by - ty * 3 * k]}
                            stroke="#1f9d55"
                            strokeWidth={2 * k}
                            lineCap="round"
                            lineJoin="round"
                            listening={false}
                          />
                        );
                      })}
                    </Group>
                  );
                  // «+» روی لبه‌های بلند (کمی دورتر از وسط)
                  if (len * scale > 110 && !dragging) {
                    const px = a[0] + (b[0] - a[0]) * 0.8;
                    const py = a[1] + (b[1] - a[1]) * 0.8;
                    out.push(
                      <Group key={`zp-${i}`} onPointerDown={(e) => addZoneCorner(e, room, i)}>
                        <Circle x={px} y={py} radius={18 * k} fill="rgba(0,0,0,0.001)" />
                        <Circle x={px} y={py} radius={8 * k} fill="#ffffff" stroke="#1f9d55" strokeWidth={1.6 * k} listening={false} />
                        <Line points={[px - 4 * k, py, px + 4 * k, py]} stroke="#1f9d55" strokeWidth={1.8 * k} listening={false} />
                        <Line points={[px, py - 4 * k, px, py + 4 * k]} stroke="#1f9d55" strokeWidth={1.8 * k} listening={false} />
                      </Group>
                    );
                  }
                });
                poly.forEach((v, i) => {
                  const prev = (i - 1 + N) % N;
                  if (flags[prev] && flags[i]) return; // گوشه‌ی بینِ دو دیوار — ثابت
                  out.push(
                    <Group key={`zc-${i}`} onPointerDown={(e) => startZoneHandle(e, room, 'zoneCorner', i)}>
                      <Circle x={v[0]} y={v[1]} radius={22 * k} fill="rgba(0,0,0,0.001)" />
                      <Circle x={v[0]} y={v[1]} radius={7 * k} fill="#1f9d55" stroke="#ffffff" strokeWidth={2 * k} listening={false} />
                    </Group>
                  );
                });
                return out;
              })()}

            {/* خطِ انگشت (حینِ کشیدن) و مرزِ صاف‌شده (منتظرِ انتخابِ طرف) */}
            {zoneStroke && zoneStroke.length > 1 && (
              <Line points={zoneStroke.flat()} stroke="#1f9d55" strokeWidth={3 / scale} lineCap="round" lineJoin="round" opacity={0.8} listening={false} />
            )}
            {zoneSide && (
              <Line points={zoneSide.pl.flat()} stroke="#1f9d55" strokeWidth={3 / scale} dash={[12 / scale, 8 / scale]} lineCap="round" listening={false} />
            )}

            {/* راهنمای چسبیدن: امتدادِ دیوار (خطِ کم‌رنگ) و نقطه‌ای که چسبیده */}
            {zoneLive &&
              (zoneLive.guides || []).map((g, k) => (
                <Line
                  key={`zg-${k}`}
                  points={g.x !== undefined ? [g.x, -100000, g.x, 100000] : [-100000, g.y, 100000, g.y]}
                  stroke="#1f9d55"
                  strokeWidth={1.2 / scale}
                  dash={[6 / scale, 5 / scale]}
                  opacity={0.7}
                  listening={false}
                  perfectDrawEnabled={false}
                />
              ))}
            {zoneLive && zoneLive.hot && (
              <Circle x={zoneLive.hot[0]} y={zoneLive.hot[1]} radius={6 / scale} fill="#1f9d55" stroke="#ffffff" strokeWidth={2 / scale} listening={false} />
            )}

            {drawingStart && previewPoint && (
              <Line
                points={[drawingStart.x, drawingStart.y, previewPoint.x, previewPoint.y]}
                stroke={activeTool === 'divider' ? '#0a66c2' : '#000000'}
                strokeWidth={activeTool === 'divider' ? 3 : WALL_THICKNESS}
                opacity={activeTool === 'divider' ? 0.7 : 0.4}
                lineCap="square"
                dash={[8, 6]}
                perfectDrawEnabled={false}
              />
            )}

            {!wallAnim && doors.map((door) => {
              const wall = walls.find((w) => w.id === door.wallId);
              if (!wall) return null;
              const placement = getItemPlacement(wall, door.ratio, door.style?.widthCm || DOOR_WIDTH);
              return (
                <Dar
                  key={door.id}
                  x={placement.x}
                  y={placement.y}
                  length={door.style?.widthCm || DOOR_WIDTH}
                  angleDeg={placement.angleDeg}
                  thickness={WALL_THICKNESS}
                  selected={selected?.type === 'door' && selected.id === door.id}
                  flip={door.facing === 'in'}
                  hingeEnd={door.hinge === 'end'}
                  onPointerDown={(e) => handleDoorWindowDown(e, 'door', door.id, door.wallId)}
                />
              );
            })}

            {windows.map((win) => {
              const wall = walls.find((w) => w.id === win.wallId);
              if (!wall) return null;
              const placement = getItemPlacement(wall, win.ratio, win.style?.widthCm || WINDOW_WIDTH);
              return (
                <Panjere
                  key={win.id}
                  x={placement.x}
                  y={placement.y}
                  length={win.style?.widthCm || WINDOW_WIDTH}
                  angleDeg={placement.angleDeg}
                  thickness={WALL_THICKNESS}
                  selected={selected?.type === 'window' && selected.id === win.id}
                  onPointerDown={(e) => handleDoorWindowDown(e, 'window', win.id, win.wallId)}
                />
              );
            })}

            {openings.map((opening) => {
              const wall = walls.find((w) => w.id === opening.wallId);
              if (!wall) return null;
              const placement = getItemPlacement(wall, opening.ratio, opening.style?.widthCm || OPENING_WIDTH);
              return (
                <Bazsho
                  key={opening.id}
                  x={placement.x}
                  y={placement.y}
                  length={opening.style?.widthCm || OPENING_WIDTH}
                  angleDeg={placement.angleDeg}
                  thickness={WALL_THICKNESS}
                  selected={selected?.type === 'opening' && selected.id === opening.id}
                  onPointerDown={(e) => handleDoorWindowDown(e, 'opening', opening.id, opening.wallId)}
                />
              );
            })}

            {/* «ورودی»: سنجاقِ سبز — با ابزارِ انتخاب جابه‌جا می‌شود، با «ورودی» در نوار جای تازه می‌گیرد */}
            {!wallAnim && entrance && (
              <Group
                x={entrance.x}
                y={entrance.y}
                scaleX={1 / scale}
                scaleY={1 / scale}
                onPointerDown={(e) => {
                  if (activeTool !== 'select') return;
                  e.cancelBubble = true;
                  setSelected({ type: 'entrance', id: 0 });
                  setDragging({ type: 'entrance' });
                }}
              >
                <Circle x={0} y={0} radius={16} fill="transparent" />
                <Shape
                  sceneFunc={(ctx, shape) => {
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.arc(0, -17, 9, Math.PI * 0.78, Math.PI * 2.22);
                    ctx.closePath();
                    ctx.fillStrokeShape(shape);
                  }}
                  fill={selected?.type === 'entrance' ? '#0a66c2' : '#1f9d55'}
                  stroke="#ffffff"
                  strokeWidth={2}
                />
                <Circle x={0} y={-17} radius={3.4} fill="#ffffff" listening={false} />
                <Rect x={-24} y={-48} width={48} height={20} cornerRadius={10} fill={selected?.type === 'entrance' ? '#0a66c2' : '#1f9d55'} />
                <Text x={-24} y={-44} width={48} align="center" text="ورودی" fontFamily="Vazirmatn, sans-serif" fontStyle="700" fontSize={11} fill="#ffffff" listening={false} />
              </Group>
            )}

            <Rect x={-2} y={-2} width={4} height={4} fill="#dbdbdb" listening={false} perfectDrawEnabled={false} />
          </Layer>
          {/* «رنگ کردنِ خانه‌ها»: بالای همه؛ یک مستطیلِ نامرئی همه‌ی لمس‌ها را می‌گیرد تا به دیوار/اتاق نرسد */}
          {zonePaint && (
            <Layer ref={paintLayerRef}>
              <Rect x={-1e6} y={-1e6} width={2e6} height={2e6} fill="rgba(0,0,0,0.001)" perfectDrawEnabled={false} />
              <Shape
                listening={false}
                perfectDrawEnabled={false}
                sceneFunc={(ctx) => {
                  const c = ctx._context;
                  const k = 1 / scale;
                  zonePaint.model.spaces.forEach((m) => {
                    c.save();
                    // فقط داخلِ فضا (کنارِ دیوارِ مورب/منحنی بیرون نزند)
                    c.beginPath();
                    (m.sp.rects && m.sp.rects.length ? m.sp.rects : [m.sp]).forEach((q) => {
                      if (q.xl0 !== undefined) {
                        c.moveTo(q.xl0, q.y1);
                        c.lineTo(q.xr0, q.y1);
                        c.lineTo(q.xr1, q.y2);
                        c.lineTo(q.xl1, q.y2);
                        c.closePath();
                      } else c.rect(q.x1, q.y1, q.x2 - q.x1, q.y2 - q.y1);
                    });
                    c.clip();
                    m.cells.forEach((cell) => {
                      const name = paintLabelsRef.current[cell.id];
                      if (!name) return;
                      c.fillStyle = paintColor(name, paintNames);
                      c.globalAlpha = 0.38;
                      c.fillRect(cell.x1, cell.y1, cell.x2 - cell.x1, cell.y2 - cell.y1);
                    });
                    c.globalAlpha = 1;
                    // خط‌های کم‌رنگِ خانه‌ها
                    c.beginPath();
                    m.xs.slice(1, -1).forEach((x) => {
                      c.moveTo(x, m.bb.y1);
                      c.lineTo(x, m.bb.y2);
                    });
                    m.ys.slice(1, -1).forEach((y) => {
                      c.moveTo(m.bb.x1, y);
                      c.lineTo(m.bb.x2, y);
                    });
                    c.strokeStyle = 'rgba(60, 80, 110, 0.35)';
                    c.lineWidth = 1.2 * k;
                    c.setLineDash([6 * k, 6 * k]);
                    c.stroke();
                    // مرزِ واقعی: هر جا دو رنگ به هم رسیدند
                    c.beginPath();
                    paintBoundaries(m, paintLabelsRef.current).forEach((g) => {
                      c.moveTo(g.x1, g.y1);
                      c.lineTo(g.x2, g.y2);
                    });
                    c.setLineDash([]);
                    c.strokeStyle = '#1e293b';
                    c.lineWidth = 2.6 * k;
                    c.lineCap = 'round';
                    c.stroke();
                    c.restore();
                  });
                  // اسم و متراژِ هر تکه
                  c.save();
                  c.textAlign = 'center';
                  c.textBaseline = 'middle';
                  paintComponents(zonePaint.model, paintLabelsRef.current).forEach((q) => {
                    const fs = 13 * k;
                    c.font = `700 ${fs}px Vazirmatn, sans-serif`;
                    const t1 = q.name;
                    const t2 = `${Math.round(q.area / 10000).toLocaleString('fa-IR')} م²`;
                    const w = Math.max(c.measureText(t1).width, c.measureText(t2).width) + 14 * k;
                    c.fillStyle = 'rgba(255,255,255,0.9)';
                    c.fillRect(q.at.px - w / 2, q.at.py - fs * 1.35, w, fs * 2.7);
                    c.fillStyle = paintColor(q.name, paintNames);
                    c.fillText(t1, q.at.px, q.at.py - fs * 0.6);
                    c.font = `400 ${fs * 0.9}px Vazirmatn, sans-serif`;
                    c.fillStyle = '#334155';
                    c.fillText(t2, q.at.px, q.at.py + fs * 0.65);
                  });
                  c.restore();
                }}
              />
            </Layer>
          )}
        </Stage>
      </div>

      {hintText && !(walls.length === 0 && !drawingStart) && <div className="create-plan-editor__hint">{hintText}</div>}

      {/* صفحه‌ی خالی: پیشنهادِ «از روی کاغذ» */}
      {walls.length === 0 && !drawingStart && !sketchOpen && (
        <div className="create-plan-editor__empty">
          <strong>پلان را روی کاغذ بکشید، راحت‌تر است</strong>
          <p>روی یک کاغذِ سفید با خودکار بکشید و عکسش را بگیرید؛ دیوارها خودشان ساخته می‌شوند.</p>
          <button type="button" onClick={() => setSketchOpen(true)}>
            📷 عکس از پلانِ کاغذی
          </button>
          <small>یا همین‌جا با ابزارِ «دیوار» شروع کنید</small>
        </div>
      )}

      {zonePaint && (
        <div className="paint-bar">
          <p className="paint-bar__lead">
            {paintBrush ? (
              <>
                انگشت را روی خانه‌های <b style={{ color: paintColor(paintBrush, paintNames) }}>«{paintBrush}»</b> بکشید (یک‌تکه) — مرز خودش ساخته
                می‌شود. یک ضربه روی خانه‌ی رنگی، رنگش را برمی‌دارد.
              </>
            ) : (
              'پاک‌کن: روی خانه‌های رنگی بکشید.'
            )}
          </p>
          <div className="paint-bar__chips">
            {paintNames.map((n) => (
              <button
                key={n}
                type="button"
                className={paintBrush === n ? 'is-on' : ''}
                style={{ '--c': paintColor(n, paintNames) }}
                onClick={() => setPaintBrush(n)}
              >
                <i />
                {n}
                {paintAreas[n] ? <small>{Math.round(paintAreas[n] / 10000).toLocaleString('fa-IR')} م²</small> : null}
              </button>
            ))}
            <button type="button" className={`paint-bar__erase${paintBrush === null ? ' is-on' : ''}`} onClick={() => setPaintBrush(null)}>
              پاک‌کن
            </button>
            {paintNameInput === null ? (
              <button type="button" className="paint-bar__add" onClick={() => setPaintNameInput('')}>
                + اسمِ دیگر
              </button>
            ) : (
              <form
                className="paint-bar__new"
                onSubmit={(e) => {
                  e.preventDefault();
                  const n = paintNameInput.trim();
                  if (n) {
                    setPaintNames((prev) => (prev.includes(n) ? prev : [...prev, n]));
                    setPaintBrush(n);
                  }
                  setPaintNameInput(null);
                }}
              >
                <input autoFocus value={paintNameInput} placeholder="مثلاً اتاق کار" onChange={(e) => setPaintNameInput(e.target.value)} />
                <button type="submit">✓</button>
              </form>
            )}
          </div>
          <div className="paint-bar__size">
            <span>اندازه‌ی خانه‌ها</span>
            <button type="button" onClick={() => changePaintStep(-1)} disabled={paintStep === PAINT_STEPS[0]} aria-label="خانه‌های ریزتر">
              −
            </button>
            <b>{paintStep >= 100 ? `${(paintStep / 100).toLocaleString('fa-IR')} متر` : `${paintStep.toLocaleString('fa-IR')} سانت`}</b>
            <button
              type="button"
              onClick={() => changePaintStep(1)}
              disabled={paintStep === PAINT_STEPS[PAINT_STEPS.length - 1]}
              aria-label="خانه‌های درشت‌تر"
            >
              +
            </button>
          </div>
          <div className="paint-bar__actions">
            <button
              type="button"
              className="paint-bar__manual"
              onClick={() => {
                closeZonePaint();
                setZoneForm(true);
              }}
            >
              کشیدنِ دستی
            </button>
            <button
              type="button"
              onClick={() => {
                closeZonePaint();
                setActiveTool('select');
              }}
            >
              انصراف
            </button>
            <button type="button" className="is-primary" onClick={applyZonePaint}>
              تأیید
            </button>
          </div>
        </div>
      )}
      {zoneForm && (
        <ZoneBox
          onStart={(name, area) => {
            setZoneForm(false);
            setZoneDraw({ name, area });
          }}
          onCancel={() => {
            setZoneForm(false);
            setActiveTool('select');
          }}
        />
      )}
      {zoneEditRoom && (
        <div className="zone-bar">
          <p>
            <b>«{zoneEditRoom.name}»</b> {Math.round(polyArea(zonePolyOf(zoneEditRoom)) / 10000).toLocaleString('fa-IR')} م²
            {zoneBaseArea > 0 && (
              <span className="zone-bar__rest">
                {' '}
                | بقیه {Math.max(0, Math.round((zoneBaseArea - polyArea(zonePolyOf(zoneEditRoom))) / 10000)).toLocaleString('fa-IR')} م²
              </span>
            )}
            <br />
            <small>↔ لبه را جابه‌جا می‌کند، ● گوشه را؛ با + گوشه‌ی تازه بسازید.</small>
          </p>
          <button
            type="button"
            onClick={() => {
              setZoneEdit(null);
              setSelected(null);
              setActiveTool('divider');
              setZoneForm(true);
            }}
          >
            قسمتِ بعدی
          </button>
          <button
            type="button"
            className="is-primary"
            onClick={() => {
              setZoneEdit(null);
              setSelected(null);
            }}
          >
            تأیید
          </button>
        </div>
      )}
      {confirmClear && (
        <div className="create-plan-editor__confirm" onClick={() => setConfirmClear(false)}>
          <div className="create-plan-editor__confirm-box" onClick={(e) => e.stopPropagation()}>
            <strong>کلِ پلان پاک شود؟</strong>
            <p>همه‌ی دیوارها، درها، پنجره‌ها، اتاق‌ها و «ورودی» حذف می‌شوند و از اول شروع می‌کنید.</p>
            <div>
              <button type="button" className="is-danger" onClick={handleClearPlan}>
                پاک کن
              </button>
              <button type="button" onClick={() => setConfirmClear(false)}>
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}

      {sketchOpen && (
        <SketchImport
          propertyArea={propertyArea || null}
          hasPlan={walls.length > 0}
          onApply={handleSketchApply}
          onClose={() => setSketchOpen(false)}
        />
      )}

      <PlanMagnifier
        pointer={magnifierPointer}
        walls={walls}
        doors={doors}
        windows={windows}
        openings={openings}
        wallThickness={WALL_THICKNESS}
        doorWidth={DOOR_WIDTH}
        windowWidth={WINDOW_WIDTH}
        openingWidth={OPENING_WIDTH}
        getItemPlacement={getItemPlacement}
        angleDeg={magnifierAngle}
        distanceLabel={magnifierDistance}
        connected={magnifierConnected}
        junction={magnifierJunction}
      />

      {selectedDoorPanel && (
        <DoorFacingBox
          x={selectedDoorPanel.screenPos.x}
          y={selectedDoorPanel.screenPos.y}
          facing={selectedDoorPanel.door.facing || 'out'}
          onChange={handleSetDoorFacing}
          hinge={selectedDoorPanel.door.hinge || 'start'}
          onFlipHinge={handleFlipDoorHinge}
        />
      )}

      {pendingRoom && (
        <OtaghNameBox
          x={pendingRoom.screenPos.x}
          y={pendingRoom.screenPos.y}
          initialName={pendingRoom.renamingId ? rooms.find((r) => r.id === pendingRoom.renamingId)?.name : ''}
          initialArea={pendingRoom.renamingId ? rooms.find((r) => r.id === pendingRoom.renamingId)?.area || null : null}
          estimate={pendingRoom.renamingId ? roomAreaOf(rooms.find((r) => r.id === pendingRoom.renamingId)) : pendingAreaEstimate}
          onConfirm={handleConfirmRoomName}
          onCancel={() => setPendingRoom(null)}
        />
      )}
    </div>
  );
}

export default CreatePlanEditor;